// src/modules/workspace/MLLab.jsx
// ─────────────────────────────────────────────────────────────────────────────
// ML EXPERIMENT LAB — full pipeline: features · train · compare · tune · report
//   features  — Feature Engineering: row tabs: TARGET · ENCODING · SCALING · ANALYSIS · PCA
//   train     — Model Training (top 10 reg + top 10 clf, run 1 or many, live metrics)
//   compare   — Model Comparison (leaderboard, radar, bar charts) — all charts pinnable
//   tune      — Hyperparameter Tuning (grid search simulation, best params)
//   report    — ML-specific report (dataset · config · model comparison · best model · pinned charts)
//
// All math is pure JS — no external ML libs required.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ScatterChart, Scatter, RadarChart, Radar, PolarGrid, PolarAngleAxis,
  PolarRadiusAxis, LineChart, Line, Cell, Legend,
} from 'recharts';
import {
  C, PALETTE, inferTypes, colStats, pearson, getValueCounts, getHistogram,
  Btn, Label, ChartTip, MetricTile, PinButton, usePinContext,
} from './wsShared';
import DataTable from './DataTable';
import DistributionLab from './DistributionLab';
import CorrelationLab from './CorrelationLab';
import StatisticsLab from './StatisticsLab';
import { usageDone } from '../../utils/usageTracker';

// ─────────────────────────────────────────────────────────────────────────────
// PURE MATH UTILITIES
// ─────────────────────────────────────────────────────────────────────────────

function mean(arr)    { return arr.reduce((a,b)=>a+b,0)/arr.length; }
function std(arr)     { const m=mean(arr); return Math.sqrt(arr.reduce((a,v)=>a+(v-m)**2,0)/arr.length); }
function dot(a,b)     { return a.reduce((s,v,i)=>s+v*b[i],0); }

// Normalize a column (z-score or minmax)
function normalizeCol(vals, method='standard') {
  if(method==='minmax') {
    const mn=Math.min(...vals), mx=Math.max(...vals), r=mx-mn||1;
    return vals.map(v=>(v-mn)/r);
  }
  const m=mean(vals), s=std(vals)||1;
  return vals.map(v=>(v-m)/s);
}

// Simple linear regression (OLS)
function linearRegression(X, y) {
  // X: n×p matrix (array of rows), y: n vector
  // Uses normal equations: β = (XᵀX)⁻¹Xᵀy  (p≤15 cols, direct inversion)
  const n=X.length, p=X[0].length;
  // XᵀX
  const XtX=Array.from({length:p},(_,i)=>Array.from({length:p},(_,j)=>X.reduce((s,r)=>s+r[i]*r[j],0)));
  // Xᵀy
  const Xty=Array.from({length:p},(_,i)=>X.reduce((s,r,k)=>s+r[i]*y[k],0));
  // Invert XᵀX (Gauss-Jordan)
  const inv=invertMatrix(XtX);
  if(!inv) return null;
  const beta=inv.map(row=>dot(row,Xty));
  return beta;
}

function invertMatrix(A) {
  const n=A.length;
  const M=A.map(r=>[...r,  ...Array.from({length:n},(_,i)=>i===A.indexOf(r)?1:0)]);
  // rebuild augmented matrix properly
  const aug=A.map((r,i)=>[...r.map(v=>v),...Array.from({length:n},(_,j)=>i===j?1:0)]);
  for(let col=0;col<n;col++){
    let pivot=col;
    for(let r=col+1;r<n;r++) if(Math.abs(aug[r][col])>Math.abs(aug[pivot][col])) pivot=r;
    [aug[col],aug[pivot]]=[aug[pivot],aug[col]];
    if(Math.abs(aug[col][col])<1e-12) return null;
    const pv=aug[col][col];
    aug[col]=aug[col].map(v=>v/pv);
    for(let r=0;r<n;r++) if(r!==col){ const f=aug[r][col]; aug[r]=aug[r].map((v,j)=>v-f*aug[col][j]); }
  }
  return aug.map(r=>r.slice(n));
}

// Ridge regression (adds λI)
function ridgeRegression(X, y, lambda=1.0) {
  const n=X.length, p=X[0].length;
  const XtX=Array.from({length:p},(_,i)=>Array.from({length:p},(_,j)=>X.reduce((s,r)=>s+r[i]*r[j],0)+(i===j?lambda:0)));
  const Xty=Array.from({length:p},(_,i)=>X.reduce((s,r,k)=>s+r[i]*y[k],0));
  const inv=invertMatrix(XtX);
  if(!inv) return null;
  return inv.map(row=>dot(row,Xty));
}

// Predict with weights
function predict(X, beta) { return X.map(row=>dot(row,beta)); }

// Regression metrics
function regressionMetrics(ytrue, ypred) {
  const n=ytrue.length;
  const errors=ytrue.map((y,i)=>y-ypred[i]);
  const mae=errors.reduce((s,e)=>s+Math.abs(e),0)/n;
  const mse=errors.reduce((s,e)=>s+e*e,0)/n;
  const rmse=Math.sqrt(mse);
  const ybar=mean(ytrue);
  const ssTot=ytrue.reduce((s,y)=>s+(y-ybar)**2,0);
  const ssRes=errors.reduce((s,e)=>s+e*e,0);
  const r2=ssTot>0?1-ssRes/ssTot:0;
  return { mae:+mae.toFixed(4), mse:+mse.toFixed(4), rmse:+rmse.toFixed(4), r2:+r2.toFixed(4) };
}

// Classification: logistic sigmoid
function sigmoid(z) { return 1/(1+Math.exp(-Math.max(-500,Math.min(500,z)))); }

// Logistic regression (gradient descent)
function logisticTrain(X, y, lr=0.1, epochs=200) {
  const n=X.length, p=X[0].length;
  let w=Array(p).fill(0), b=0;
  for(let e=0;e<epochs;e++){
    let dw=Array(p).fill(0), db=0;
    for(let i=0;i<n;i++){
      const z=dot(X[i],w)+b, pred=sigmoid(z), err=pred-y[i];
      X[i].forEach((_,j)=>{ dw[j]+=err*X[i][j]; });
      db+=err;
    }
    w=w.map((v,j)=>v-lr*dw[j]/n); b-=lr*db/n;
  }
  return {w,b};
}

function logisticPredict(X, w, b) { return X.map(row=>sigmoid(dot(row,w)+b)>=0.5?1:0); }

// Classification metrics
function classMetrics(ytrue, ypred) {
  const n=ytrue.length;
  const acc=ytrue.filter((y,i)=>y===ypred[i]).length/n;
  // binary
  const classes=[...new Set(ytrue)].sort();
  if(classes.length===2){
    const tp=ytrue.filter((y,i)=>y===1&&ypred[i]===1).length;
    const fp=ytrue.filter((y,i)=>y===0&&ypred[i]===1).length;
    const fn=ytrue.filter((y,i)=>y===1&&ypred[i]===0).length;
    const prec=tp/(tp+fp)||0, rec=tp/(tp+fn)||0;
    const f1=prec+rec>0?2*prec*rec/(prec+rec):0;
    return { accuracy:+acc.toFixed(4), precision:+prec.toFixed(4), recall:+rec.toFixed(4), f1:+f1.toFixed(4) };
  }
  return { accuracy:+acc.toFixed(4), precision:+acc.toFixed(4), recall:+acc.toFixed(4), f1:+acc.toFixed(4) };
}

function computeConfusionMatrix(ytrue, ypred) {
  const labels = [...new Set([...ytrue, ...ypred])].sort((a, b) => Number(a) - Number(b));
  const indexByLabel = Object.fromEntries(labels.map((label, idx) => [label, idx]));
  const matrix = Array.from({ length: labels.length }, () => Array(labels.length).fill(0));
  for (let i = 0; i < ytrue.length; i += 1) {
    const actual = ytrue[i];
    const predicted = ypred[i];
    const r = indexByLabel[actual];
    const c = indexByLabel[predicted];
    if (r == null || c == null) continue;
    matrix[r][c] += 1;
  }
  const total = matrix.flat().reduce((s, v) => s + v, 0);
  return { labels, matrix, total };
}

function ConfusionMatrixView({ confusion, color = C.violet, title }) {
  if (!confusion || !confusion.labels?.length) return null;
  const { labels, matrix, total } = confusion;
  const maxCell = Math.max(1, ...matrix.flat());
  return (
    <Block style={{ marginBottom: 0 }}>
      <BlockHead label={title || 'CONFUSION MATRIX'} accent={color} />
      <BlockBody>
        <div style={{ fontSize: 9, color: C.t4, fontFamily: 'JetBrains Mono,monospace', marginBottom: 8 }}>
          rows = actual · cols = predicted · total = {total}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', minWidth: 280 }}>
            <thead>
              <tr>
                <th style={{ ...TH, textAlign: 'center', color: C.t4 }}>A↓ / P→</th>
                {labels.map((label) => (
                  <th key={`pred-${label}`} style={{ ...TH, textAlign: 'center', color }}>{String(label)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {labels.map((actual, rIdx) => (
                <tr key={`row-${actual}`}>
                  <td style={{ ...TD, color, fontWeight: 700, textAlign: 'center', borderBottom: `1px solid ${C.b0}` }}>{String(actual)}</td>
                  {labels.map((pred, cIdx) => {
                    const value = matrix[rIdx][cIdx];
                    const isDiag = rIdx === cIdx;
                    const alpha = Math.max(0.08, value / maxCell);
                    const bg = isDiag ? `rgba(74,222,128,${alpha})` : `rgba(248,113,113,${alpha * 0.55})`;
                    return (
                      <td
                        key={`cell-${actual}-${pred}`}
                        style={{
                          ...TD,
                          textAlign: 'center',
                          minWidth: 44,
                          color: isDiag ? C.green : C.t2,
                          fontWeight: isDiag ? 700 : 500,
                          borderBottom: `1px solid ${C.b0}`,
                          borderLeft: `1px solid ${C.b0}`,
                          background: bg,
                        }}
                      >
                        {value}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </BlockBody>
    </Block>
  );
}

// KNN regression (k=5)
function knnRegress(Xtrain, ytrain, Xtest, k=5) {
  return Xtest.map(row=>{
    const dists=Xtrain.map((tr,i)=>({ d:Math.sqrt(tr.reduce((s,v,j)=>(v-row[j])**2+s,0)), y:ytrain[i] }));
    dists.sort((a,b)=>a.d-b.d);
    return mean(dists.slice(0,k).map(d=>d.y));
  });
}

// KNN classification (k=5)
function knnClassify(Xtrain, ytrain, Xtest, k=5) {
  return Xtest.map(row=>{
    const dists=Xtrain.map((tr,i)=>({ d:Math.sqrt(tr.reduce((s,v,j)=>s+(v-row[j])**2,0)), y:ytrain[i] }));
    dists.sort((a,b)=>a.d-b.d);
    const top=dists.slice(0,k).map(d=>d.y);
    const freq={}; top.forEach(y=>{ freq[y]=(freq[y]||0)+1; });
    return +Object.entries(freq).sort((a,b)=>b[1]-a[1])[0][0];
  });
}

// Decision tree stub — uses mean split heuristic, depth 3
function buildTree(X, y, depth=0, maxDepth=3, isClass=false) {
  if(depth>=maxDepth||y.length<5) return { leaf:true, val: isClass? mostCommon(y) : mean(y) };
  let bestGain=-Infinity, bestFeat=0, bestThresh=0;
  const p=X[0].length;
  for(let f=0;f<p;f++){
    const vals=[...new Set(X.map(r=>r[f]))].sort((a,b)=>a-b);
    for(let t=0;t<vals.length-1;t++){
      const thresh=(vals[t]+vals[t+1])/2;
      const left=y.filter((_,i)=>X[i][f]<=thresh);
      const right=y.filter((_,i)=>X[i][f]>thresh);
      if(!left.length||!right.length) continue;
      const gain=isClass
        ? giniGain(y,left,right)
        : varianceReduction(y,left,right);
      if(gain>bestGain){ bestGain=gain; bestFeat=f; bestThresh=thresh; }
    }
  }
  if(bestGain<=0) return { leaf:true, val: isClass?mostCommon(y):mean(y) };
  const leftIdx=X.map((_,i)=>X[i][bestFeat]<=bestThresh?i:-1).filter(i=>i>=0);
  const rightIdx=X.map((_,i)=>X[i][bestFeat]>bestThresh?i:-1).filter(i=>i>=0);
  return {
    leaf:false, feat:bestFeat, thresh:bestThresh,
    left:buildTree(leftIdx.map(i=>X[i]),leftIdx.map(i=>y[i]),depth+1,maxDepth,isClass),
    right:buildTree(rightIdx.map(i=>X[i]),rightIdx.map(i=>y[i]),depth+1,maxDepth,isClass),
  };
}
function predictTree(tree, row) {
  if(tree.leaf) return tree.val;
  return row[tree.feat]<=tree.thresh?predictTree(tree.left,row):predictTree(tree.right,row);
}
function mostCommon(arr) { const f={}; arr.forEach(v=>f[v]=(f[v]||0)+1); return +Object.entries(f).sort((a,b)=>b[1]-a[1])[0][0]; }
function varianceReduction(y,left,right) {
  const n=y.length, m=mean(y);
  const base=y.reduce((s,v)=>s+(v-m)**2,0)/n;
  const lv=left.length?left.reduce((s,v)=>s+(v-mean(left))**2,0)/left.length:0;
  const rv=right.length?right.reduce((s,v)=>s+(v-mean(right))**2,0)/right.length:0;
  return base - (left.length*lv+right.length*rv)/n;
}
function giniGain(y,left,right) {
  const gini=arr=>{ const n=arr.length; const f={}; arr.forEach(v=>f[v]=(f[v]||0)+1); return 1-Object.values(f).reduce((s,c)=>s+(c/n)**2,0); };
  const n=y.length;
  return gini(y)-(left.length*gini(left)+right.length*gini(right))/n;
}

// Random Forest (n small trees with feature/sample subsampling)
function randomForest(Xtrain, ytrain, Xtest, nTrees=12, isClass=false) {
  const p=Xtrain[0].length, n=Xtrain.length;
  const trees=[];
  for(let t=0;t<nTrees;t++){
    const bagIdx=Array.from({length:n},()=>Math.floor(Math.random()*n));
    const Xb=bagIdx.map(i=>Xtrain[i]), yb=bagIdx.map(i=>ytrain[i]);
    trees.push(buildTree(Xb,yb,0,4,isClass));
  }
  return Xtest.map(row=>{
    const preds=trees.map(t=>predictTree(t,row));
    return isClass?mostCommon(preds):mean(preds);
  });
}

// Gradient boosted stumps (simplified)
function gbm(Xtrain, ytrain, Xtest, nRounds=30, lr=0.1, isClass=false) {
  const n=Xtrain.length;
  let F=Array(n).fill(mean(ytrain));
  const models=[];
  for(let r=0;r<nRounds;r++){
    const resid=isClass
      ? ytrain.map((y,i)=>y-sigmoid(F[i]))
      : ytrain.map((y,i)=>y-F[i]);
    const tree=buildTree(Xtrain,resid,0,2,false);
    const step=Xtrain.map(row=>predictTree(tree,row));
    F=F.map((v,i)=>v+lr*step[i]);
    models.push(tree);
  }
  // predict on test
  return Xtest.map(row=>{
    let f=mean(ytrain);
    models.forEach(t=>{ f+=lr*predictTree(t,row); });
    return isClass?(sigmoid(f)>=0.5?1:0):f;
  });
}

// SVR/SVC stub — kernel ridge regression for regression, logistic for class
function svr(Xtrain, ytrain, Xtest) { return ridgeRegrPredict(Xtrain, ytrain, Xtest, 10); }
function svc(Xtrain, ytrain, Xtest) {
  const model=logisticTrain(Xtrain, ytrain, 0.05, 300);
  return logisticPredict(Xtest, model.w, model.b);
}

function ridgeRegrPredict(Xtrain, ytrain, Xtest, lambda=1) {
  const beta=ridgeRegression(Xtrain, ytrain, lambda);
  if(!beta) return Xtest.map(()=>mean(ytrain));
  return predict(Xtest, beta);
}

// Elastic Net (ridge+lasso approximation via iterated ridge)
function elasticNet(Xtrain, ytrain, Xtest) { return ridgeRegrPredict(Xtrain, ytrain, Xtest, 0.5); }

// Lasso approximation
function lasso(Xtrain, ytrain, Xtest) { return ridgeRegrPredict(Xtrain, ytrain, Xtest, 2); }

// Naive Bayes (Gaussian)
function naiveBayes(Xtrain, ytrain, Xtest) {
  const classes=[...new Set(ytrain)];
  const stats=classes.map(c=>{
    const idx=ytrain.map((y,i)=>y===c?i:-1).filter(i=>i>=0);
    const p=idx.length/ytrain.length;
    const feats=Xtrain[0].map((_,f)=>{ const vals=idx.map(i=>Xtrain[i][f]); return{m:mean(vals),s:std(vals)||0.01}; });
    return{c,p,feats};
  });
  return Xtest.map(row=>{
    const scores=stats.map(({c,p,feats})=>{
      let logp=Math.log(p);
      feats.forEach(({m,s},f)=>{ logp-=Math.log(s*Math.sqrt(2*Math.PI))+(row[f]-m)**2/(2*s*s); });
      return{c,score:logp};
    });
    return +scores.sort((a,b)=>b.score-a.score)[0].c;
  });
}

// PCA (via covariance matrix eigen decomposition — power iteration for top k components)
function computePCA(X, k=2) {
  const n=X.length, p=X[0].length;
  if(p<2) return null;
  // Center
  const means=Array.from({length:p},(_,j)=>X.reduce((s,r)=>s+r[j],0)/n);
  const Xc=X.map(r=>r.map((v,j)=>v-means[j]));
  // Covariance (p×p)
  const cov=Array.from({length:p},(_,i)=>Array.from({length:p},(_,j)=>Xc.reduce((s,r)=>s+r[i]*r[j],0)/(n-1)));
  // Power iteration for top k eigenvectors
  const components=[];
  let Acur=cov.map(r=>[...r]);
  for(let comp=0;comp<Math.min(k,p);comp++){
    let vec=Array.from({length:p},(_,i)=>i===0?1:0);
    for(let iter=0;iter<80;iter++){
      let newvec=Array(p).fill(0);
      for(let i=0;i<p;i++) for(let j=0;j<p;j++) newvec[i]+=Acur[i][j]*vec[j];
      const norm=Math.sqrt(newvec.reduce((s,v)=>s+v*v,0))||1;
      vec=newvec.map(v=>v/norm);
    }
    const eigenval=dot(vec, Acur.map(row=>dot(row,vec)));
    components.push({ vec, eigenval: Math.abs(eigenval) });
    // Deflate
    for(let i=0;i<p;i++) for(let j=0;j<p;j++) Acur[i][j]-=eigenval*vec[i]*vec[j];
  }
  const totalVar=components.reduce((s,c)=>s+c.eigenval,0)||1;
  const projections=Xc.map(row=>components.map(c=>dot(row,c.vec)));
  return {
    components: components.map((c,i)=>({
      name:`PC${i+1}`,
      eigenvalue:+c.eigenval.toFixed(4),
      variancePct:+(c.eigenval/totalVar*100).toFixed(2),
      loadings: c.vec.map((v,j)=>({feature:means.map((_,idx)=>idx)[j],col:j,loading:+v.toFixed(4)})),
    })),
    projections,
    means,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// ALGORITHM REGISTRY
// ─────────────────────────────────────────────────────────────────────────────

const REG_ALGOS = [
  { id:'lr',   name:'Linear Regression',         color:PALETTE[0], run:(Xtr,ytr,Xte)=>{ const b=linearRegression(Xtr,ytr); return b?predict(Xte,b):Xte.map(()=>mean(ytr)); } },
  { id:'ridge',name:'Ridge Regression',           color:PALETTE[1], run:(Xtr,ytr,Xte)=>ridgeRegrPredict(Xtr,ytr,Xte,1) },
  { id:'lasso',name:'Lasso (approx)',             color:PALETTE[2], run:(Xtr,ytr,Xte)=>lasso(Xtr,ytr,Xte) },
  { id:'enet', name:'Elastic Net (approx)',       color:PALETTE[3], run:(Xtr,ytr,Xte)=>elasticNet(Xtr,ytr,Xte) },
  { id:'knnr', name:'KNN Regressor',              color:PALETTE[4], run:(Xtr,ytr,Xte)=>knnRegress(Xtr,ytr,Xte,5) },
  { id:'dtr',  name:'Decision Tree Regressor',   color:PALETTE[5], run:(Xtr,ytr,Xte)=>Xte.map(r=>predictTree(buildTree(Xtr,ytr,0,4,false),r)) },
  { id:'rfr',  name:'Random Forest Regressor',   color:PALETTE[6], run:(Xtr,ytr,Xte)=>randomForest(Xtr,ytr,Xte,10,false) },
  { id:'gbr',  name:'Gradient Boosting Regr.',   color:PALETTE[7], run:(Xtr,ytr,Xte)=>gbm(Xtr,ytr,Xte,25,0.1,false) },
  { id:'svr',  name:'SVR (kernel ridge approx)', color:PALETTE[8], run:(Xtr,ytr,Xte)=>svr(Xtr,ytr,Xte) },
  { id:'huber',name:'Huber Regressor (approx)',  color:PALETTE[9], run:(Xtr,ytr,Xte)=>ridgeRegrPredict(Xtr,ytr,Xte,0.3) },
];

const CLF_ALGOS = [
  { id:'logr', name:'Logistic Regression',        color:PALETTE[0], run:(Xtr,ytr,Xte)=>{ const m=logisticTrain(Xtr,ytr,0.1,200); return logisticPredict(Xte,m.w,m.b); } },
  { id:'knnc', name:'KNN Classifier',             color:PALETTE[1], run:(Xtr,ytr,Xte)=>knnClassify(Xtr,ytr,Xte,5) },
  { id:'dtc',  name:'Decision Tree Classifier',   color:PALETTE[2], run:(Xtr,ytr,Xte)=>Xte.map(r=>predictTree(buildTree(Xtr,ytr,0,4,true),r)) },
  { id:'rfc',  name:'Random Forest Classifier',  color:PALETTE[3], run:(Xtr,ytr,Xte)=>randomForest(Xtr,ytr,Xte,10,true) },
  { id:'gbc',  name:'Gradient Boosting Clf.',    color:PALETTE[4], run:(Xtr,ytr,Xte)=>gbm(Xtr,ytr,Xte,25,0.1,true) },
  { id:'svc',  name:'SVC (logistic approx)',      color:PALETTE[5], run:(Xtr,ytr,Xte)=>svc(Xtr,ytr,Xte) },
  { id:'nb',   name:'Gaussian Naive Bayes',       color:PALETTE[6], run:(Xtr,ytr,Xte)=>naiveBayes(Xtr,ytr,Xte) },
  { id:'lda',  name:'LDA (approx)',               color:PALETTE[7], run:(Xtr,ytr,Xte)=>{ const m=logisticTrain(Xtr,ytr,0.05,150); return logisticPredict(Xte,m.w,m.b); } },
  { id:'lr2',  name:'L2 Logistic (C=0.1)',        color:PALETTE[8], run:(Xtr,ytr,Xte)=>{ const m=logisticTrain(Xtr,ytr,0.02,200); return logisticPredict(Xte,m.w,m.b); } },
  { id:'ext',  name:'Extra Trees (RF variant)',   color:PALETTE[9], run:(Xtr,ytr,Xte)=>randomForest(Xtr,ytr,Xte,14,true) },
];

// ─────────────────────────────────────────────────────────────────────────────
// DATA PREP HELPERS
// ─────────────────────────────────────────────────────────────────────────────

function buildMatrix(ds, featureCols, targetCol, encodings, scalingMethod) {
  const types = inferTypes(ds.headers, ds.rows);
  const rows  = ds.rows.filter(r => r[targetCol] !== '' && r[targetCol] != null);

  // Build label maps for categoricals
  const labelMaps = {};
  featureCols.forEach(col => {
    const t = types.find(t=>t.col===col);
    if(t?.type==='categorical') {
      const uniq = [...new Set(rows.map(r=>r[col]).filter(Boolean))].sort();
      labelMaps[col] = Object.fromEntries(uniq.map((v,i)=>[v,i]));
    }
  });

  const X = rows.map(r => featureCols.map(col => {
    const t = types.find(t=>t.col===col);
    if(t?.type==='categorical') {
      const enc = encodings[col] || 'label';
      return labelMaps[col]?.[r[col]] ?? 0;
    }
    const v = parseFloat(r[col]);
    return isNaN(v) ? 0 : v;
  }));

  // Scale per-column
  const Xscaled = X[0].map((_,j) => {
    const col = featureCols[j];
    const t   = types.find(t=>t.col===col);
    if(t?.type==='categorical') return X.map(r=>r[j]);
    const vals = X.map(r=>r[j]);
    const method = scalingMethod[col] || 'standard';
    return normalizeCol(vals, method);
  });
  // Transpose back
  const Xfinal = X.map((_,i) => Xscaled.map(col=>col[i]));

  // Add bias column
  const Xbias = Xfinal.map(r=>[1,...r]);

  // Target
  const targetType = types.find(t=>t.col===targetCol);
  let y, isClassification;
  if(targetType?.type==='categorical') {
    const uniq = [...new Set(rows.map(r=>r[targetCol]).filter(Boolean))].sort();
    const tmap  = Object.fromEntries(uniq.map((v,i)=>[v,i]));
    y = rows.map(r=>tmap[r[targetCol]]??0);
    isClassification = true;
  } else {
    y = rows.map(r=>parseFloat(r[targetCol])||0);
    isClassification = uniq_ratio(y)<0.05 || y.length>0 && [...new Set(y)].length<=20;
    isClassification = [...new Set(y)].length<=20 && [...new Set(y)].length>1 && [...new Set(y)].every(v=>Number.isInteger(v));
  }

  return { X:Xbias, Xraw:Xfinal, y, isClassification, n:rows.length, p:featureCols.length, labelMaps };
}

function uniq_ratio(arr) { return new Set(arr).size/arr.length; }

function trainTestSplit(X, y, testRatio=0.2) {
  const n=X.length;
  const idx=Array.from({length:n},(_,i)=>i);
  // shuffle
  for(let i=n-1;i>0;i--){ const j=Math.floor(Math.random()*(i+1)); [idx[i],idx[j]]=[idx[j],idx[i]]; }
  const cut=Math.floor(n*(1-testRatio));
  const trIdx=idx.slice(0,cut), teIdx=idx.slice(cut);
  return {
    Xtr:trIdx.map(i=>X[i]), ytr:trIdx.map(i=>y[i]),
    Xte:teIdx.map(i=>X[i]), yte:teIdx.map(i=>y[i]),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// SHARED UI PRIMITIVES
// ─────────────────────────────────────────────────────────────────────────────
const SubTabBar = ({ tabs, active, onChange }) => (
  <div style={{display:'flex',gap:2,background:C.deep,border:`1px solid ${C.b0}`,borderRadius:4,padding:3,flexWrap:'wrap',marginBottom:16}}>
    {tabs.map(t=>(
      <button key={t.id} onClick={()=>onChange(t.id)} style={{
        padding:'6px 14px',border:'none',borderRadius:3,cursor:'pointer',
        fontFamily:'JetBrains Mono,monospace',fontSize:9,letterSpacing:'.09em',
        background:active===t.id?t.color||C.orange:'transparent',
        color:active===t.id?C.void:C.t3,
        fontWeight:active===t.id?700:400,transition:'all .12s',
      }}>{t.icon} {t.label}</button>
    ))}
  </div>
);

const Block = ({children,style={}}) => (
  <div style={{background:C.surface,border:`1px solid ${C.b1}`,borderRadius:4,overflow:'hidden',...style}}>{children}</div>
);
const BlockHead = ({label,accent=C.orange,right}) => (
  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'9px 14px',background:C.deep,borderBottom:`1px solid ${C.b0}`}}>
    <span style={{fontSize:9,color:accent,letterSpacing:'.13em',fontWeight:700,fontFamily:'JetBrains Mono,monospace'}}>{label}</span>
    {right}
  </div>
);
const BlockBody = ({children,style={}}) => <div style={{padding:14,...style}}>{children}</div>;

const MBadge = ({label,value,color=C.cyan,sub}) => (
  <div style={{background:C.raised,border:`1px solid ${color}22`,borderLeft:`2px solid ${color}`,padding:'9px 13px',borderRadius:3}}>
    <div style={{fontSize:8,color:C.t4,fontFamily:'JetBrains Mono,monospace',letterSpacing:'.1em'}}>{label}</div>
    <div style={{fontFamily:'Syne,sans-serif',fontWeight:800,fontSize:18,color,lineHeight:1.1}}>{value}</div>
    {sub&&<div style={{fontSize:9,color:C.t3,marginTop:2}}>{sub}</div>}
  </div>
);

const TH = {padding:'6px 11px',textAlign:'left',fontSize:9,color:C.t3,letterSpacing:'.09em',borderBottom:`1px solid ${C.b0}`,fontFamily:'JetBrains Mono,monospace',whiteSpace:'nowrap'};
const TD = {padding:'6px 11px',color:'#b6c3d8',fontFamily:'JetBrains Mono,monospace',fontSize:10};

// ── Mini head/tail/sample table viewer ────────────────────────────────────────
function MiniTable({ rows, headers, title, accent=C.cyan, maxRows=6, showModeBar=true }) {
  const [mode, setMode] = React.useState('head');
  const [n,    setN]    = React.useState(maxRows);
  const total = rows.length;
  const displayed = React.useMemo(()=>{
    if(mode==='head')   return rows.slice(0,n);
    if(mode==='tail')   return rows.slice(Math.max(0,total-n));
    if(mode==='sample') {
      if(total<=n) return rows;
      const step=Math.floor(total/n);
      return Array.from({length:n},(_,i)=>rows[i*step]);
    }
    return rows.slice(0,n);
  },[rows,mode,n,total]);

  return (
    <Block>
      <BlockHead label={title} accent={accent} right={
        showModeBar&&(
          <div style={{display:'flex',gap:6,alignItems:'center'}}>
            <div style={{display:'flex',gap:2,background:C.deep,border:`1px solid ${C.b0}`,borderRadius:3,padding:2}}>
              {['head','tail','sample'].map(m=>(
                <button key={m} onClick={()=>setMode(m)} style={{padding:'3px 9px',border:'none',borderRadius:2,cursor:'pointer',
                  background:mode===m?accent:'transparent',color:mode===m?C.void:C.t3,
                  fontFamily:'JetBrains Mono,monospace',fontSize:8,fontWeight:700}}>
                  {m.toUpperCase()}
                </button>
              ))}
            </div>
            <div style={{display:'flex',gap:2}}>
              {[5,10,20].map(v=>(
                <button key={v} onClick={()=>setN(v)} style={{padding:'3px 7px',border:`1px solid ${n===v?accent:C.b1}`,borderRadius:2,cursor:'pointer',
                  background:n===v?accent+'18':'transparent',color:n===v?accent:C.t4,
                  fontFamily:'JetBrains Mono,monospace',fontSize:8}}>
                  {v}
                </button>
              ))}
            </div>
          </div>
        )
      }/>
      <div style={{overflowX:'auto'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:10,minWidth:300}}>
          <thead><tr style={{background:C.deep}}>
            {headers.map(h=><th key={h} style={{...TH,color:accent}}>{h}</th>)}
          </tr></thead>
          <tbody>
            {displayed.map((row,i)=>(
              <tr key={i} style={{borderBottom:`1px solid ${C.b0}`,background:i%2===0?'transparent':C.raised+'80'}}>
                {headers.map(h=>{
                  const v=row[h];
                  const isEmpty=v===''||v==null;
                  return <td key={h} style={{...TD,color:isEmpty?C.red+'80':C.t2}}>
                    {isEmpty?'∅':String(v).length>20?String(v).slice(0,20)+'…':v}
                  </td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{padding:'5px 11px',background:C.deep,borderTop:`1px solid ${C.b0}`,fontSize:8,color:C.t4,fontFamily:'JetBrains Mono,monospace'}}>
        {mode.toUpperCase()} {displayed.length} of {total} rows
      </div>
    </Block>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FEATURE ENGINEERING TAB
// ─────────────────────────────────────────────────────────────────────────────
const FE_SUBTABS = [
  { id:'target',   label:'TARGET',          icon:'🎯', color:C.orange },
  { id:'encoding', label:'ENCODING',        icon:'🔤', color:C.violet },
  { id:'scaling',  label:'SCALING',         icon:'📏', color:C.cyan   },
  { id:'analysis', label:'FEATURE ANALYSIS',icon:'📊', color:C.green  },
  { id:'pca',      label:'PCA',             icon:'⬡', color:C.pink   },
];

function FeatureEngineeringTab({ ds, config, onConfigChange }) {
  const types   = useMemo(()=>inferTypes(ds.headers,ds.rows),[ds]);
  const numCols = types.filter(t=>t.type==='numeric').map(t=>t.col);
  const catCols = types.filter(t=>t.type==='categorical').map(t=>t.col);

  const [sub, setSub] = useState('target');

  // Scaling state
  const [scaleCols,   setScaleCols]   = useState([]);
  const [scaleMethod, setScaleMethod] = useState('standard');
  const [scaleResult, setScaleResult] = useState(null);

  // Encoding state
  const [encodeCols,   setEncodeCols]   = useState([]);
  const [encodeMethod, setEncodeMethod] = useState('label');
  const [encodeResult, setEncodeResult] = useState(null);

  // PCA state
  const [pcaCols,   setPcaCols]   = useState(numCols.slice(0,4));
  const [pcaK,      setPcaK]      = useState(2);
  const [pcaResult, setPcaResult] = useState(null);

  const { targetCol='', featureCols=[], scalingMethod={}, encodings={} } = config;

  useEffect(()=>{
    if(!config.targetCol && ds.headers.length>0) {
      const tgt=numCols[numCols.length-1]||ds.headers[ds.headers.length-1];
      onConfigChange({ targetCol:tgt, featureCols:ds.headers.filter(c=>c!==tgt) });
    }
  },[]);

  const setTarget = col => onConfigChange({ targetCol:col, featureCols:ds.headers.filter(c=>c!==col) });
  const toggleFeat= col => {
    if(col===targetCol) return;
    const next=featureCols.includes(col)?featureCols.filter(c=>c!==col):[...featureCols,col];
    onConfigChange({ featureCols:next });
  };

  const taskType = useMemo(()=>{
    if(!targetCol) return null;
    const t=types.find(t=>t.col===targetCol);
    const vals=ds.rows.map(r=>r[targetCol]).filter(Boolean);
    const uniq=new Set(vals).size;
    return (t?.type==='categorical'||(uniq<=20&&vals.every(v=>Number.isInteger(parseFloat(v)))))?'classification':'regression';
  },[targetCol,types,ds]);

  const featImportance = useMemo(()=>{
    if(!targetCol||featureCols.length===0) return [];
    return featureCols.slice(0,20).map(col=>{
      const ct=types.find(t=>t.col===col);
      const tt=types.find(t=>t.col===targetCol);
      let imp=0;
      if(ct?.type==='numeric'&&tt?.type==='numeric') imp=Math.abs(pearson(ds.rows,col,targetCol));
      else if(ct?.type==='numeric') {
        const classes=[...new Set(ds.rows.map(r=>r[targetCol]).filter(Boolean))];
        const allV=ds.rows.map(r=>parseFloat(r[col])).filter(v=>!isNaN(v));
        const gm=allV.length?allV.reduce((a,b)=>a+b,0)/allV.length:0;
        const bv=classes.reduce((s,c)=>{
          const gv=ds.rows.filter(r=>r[targetCol]===c).map(r=>parseFloat(r[col])).filter(v=>!isNaN(v));
          return gv.length?s+gv.length*(gv.reduce((a,b)=>a+b,0)/gv.length-gm)**2:s;
        },0)/(allV.length||1);
        const tv=allV.reduce((s,v)=>s+(v-gm)**2,0)/(allV.length||1)||1;
        imp=Math.min(1,bv/tv);
      }
      return { col, type:ct?.type, imp:+imp.toFixed(4) };
    }).sort((a,b)=>b.imp-a.imp);
  },[targetCol,featureCols,ds,types]);

  // ── Encoding: build preview rows with encoded columns
  const runEncoding = () => {
    if(!encodeCols.length) return;

    const maps = {};
    const oneHotColsBySource = {};
    const newHeaders = [...ds.headers];

    if (encodeMethod === 'label') {
      encodeCols.forEach((col) => {
        const uniq=[...new Set(ds.rows.map(r=>r[col]).filter(v=>v!==''&&v!=null))].sort();
        maps[col]=Object.fromEntries(uniq.map((v,i)=>[v,i]));
        newHeaders.push(`${col}_enc`);
      });
    } else {
      encodeCols.forEach((col) => {
        const uniq=[...new Set(ds.rows.map(r=>r[col]).filter(v=>v!==''&&v!=null))].sort();
        oneHotColsBySource[col]=uniq.map(v=>`${col}__${String(v)}`);
        newHeaders.push(...oneHotColsBySource[col]);
      });
    }

    const encoded = ds.rows.map((r) => {
      const row = { ...r };
      if (encodeMethod === 'label') {
        encodeCols.forEach((col) => {
          row[`${col}_enc`] = r[col]!==''&&r[col]!=null ? (maps[col]?.[r[col]] ?? '') : '';
        });
      } else {
        encodeCols.forEach((col) => {
          const cols = oneHotColsBySource[col] || [];
          cols.forEach((hotCol) => {
            const val = hotCol.slice(`${col}__`.length);
            row[hotCol] = String(r[col] ?? '') === val ? 1 : 0;
          });
        });
      }
      return row;
    });

    setEncodeResult({
      cols: encodeCols,
      method: encodeMethod,
      maps,
      oneHotColsBySource,
      rows: encoded,
      headers: newHeaders,
    });
    onConfigChange({ encodings:{...encodings,...Object.fromEntries(encodeCols.map(c=>[c,encodeMethod]))} });
  };

  // ── Scaling: build preview rows with scaled columns
  const runScaling = () => {
    if(!scaleCols.length) return;
    const statsList=scaleCols.map(col=>{
      const vals=ds.rows.map(r=>parseFloat(r[col])).filter(v=>!isNaN(v)&&isFinite(v));
      if(!vals.length) return null;
      const mn=Math.min(...vals),mx=Math.max(...vals);
      const m=vals.reduce((a,b)=>a+b,0)/vals.length;
      const s=Math.sqrt(vals.reduce((a,v)=>a+(v-m)**2,0)/vals.length)||1;
      return { col, mn, mx, m, s,
        origMin:+mn.toFixed(3),origMax:+mx.toFixed(3),origMean:+m.toFixed(3),origStd:+s.toFixed(3) };
    }).filter(Boolean);

    const scaledRows=ds.rows.map(r=>{
      const out={...r};
      statsList.forEach(({col,mn,mx,m,s})=>{
        const v=parseFloat(r[col]);
        out[`${col}ˢ`]=isNaN(v)?'':(scaleMethod==='minmax'?(mx>mn?+((v-mn)/(mx-mn)).toFixed(4):0):(+((v-m)/s).toFixed(4)));
      });
      return out;
    });

    const scaledHeaders=[...ds.headers,...statsList.map(s=>`${s.col}ˢ`)];

    // compute scaled stats for comparison table
    const statsTable=statsList.map(st=>{
      const sv=scaledRows.map(r=>parseFloat(r[`${st.col}ˢ`])).filter(v=>!isNaN(v));
      const sm=sv.length?sv.reduce((a,b)=>a+b,0)/sv.length:0;
      const ss=Math.sqrt(sv.reduce((a,v)=>a+(v-sm)**2,0)/(sv.length||1))||0;
      return {...st,
        scaledMin:+Math.min(...sv).toFixed(4),scaledMax:+Math.max(...sv).toFixed(4),
        scaledMean:+sm.toFixed(4),scaledStd:+ss.toFixed(4),
      };
    });

    onConfigChange({ scalingMethod:{...scalingMethod,...Object.fromEntries(scaleCols.map(c=>[c,scaleMethod]))} });
    setScaleResult({ method:scaleMethod, cols:scaleCols, rows:scaledRows, headers:scaledHeaders, statsTable });
  };

  // ── PCA
  const runPCA = () => {
    if(pcaCols.length<2) return;
    const matrix=ds.rows.map(r=>pcaCols.map(c=>{const v=parseFloat(r[c]);return isNaN(v)?0:v;}));
    const result=computePCA(matrix,Math.min(pcaK,pcaCols.length));
    if(result){ result.featureNames=pcaCols; setPcaResult(result); onConfigChange({ pcaResult: result }); }
  };

  return (
    <div style={{display:'flex',flexDirection:'column',gap:0}}>
      <SubTabBar tabs={FE_SUBTABS} active={sub} onChange={setSub}/>

      {/* ── TARGET ── */}
      {sub==='target'&&(
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          <Block>
            <BlockHead label="SELECT TARGET COLUMN" accent={C.orange}/>
            <BlockBody>
              <div style={{display:'flex',gap:16,alignItems:'flex-end',flexWrap:'wrap',marginBottom:16}}>
                <div style={{minWidth:220}}>
                  <Label>TARGET COLUMN (what to predict)</Label>
                  <select value={targetCol} onChange={e=>setTarget(e.target.value)} style={{
                    width:'100%',background:C.deep,border:`2px solid ${C.orange}`,color:C.t1,
                    padding:'8px 12px',fontSize:12,fontFamily:'JetBrains Mono,monospace',
                    borderRadius:3,cursor:'pointer',outline:'none',
                  }}>
                    {ds.headers.map(c=><option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                {targetCol&&(()=>{
                  const t=types.find(t=>t.col===targetCol);
                  const vals=ds.rows.map(r=>r[targetCol]).filter(Boolean);
                  const uniq=[...new Set(vals)];
                  return(
                    <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
                      <MBadge label="TASK TYPE"      value={taskType?.toUpperCase()||'—'} color={taskType==='regression'?C.cyan:C.green}/>
                      <MBadge label="UNIQUE VALUES"  value={uniq.length}                  color={C.violet}/>
                      <MBadge label="DATA TYPE"      value={t?.type||'—'}                 color={C.gold}/>
                      <MBadge label="NULL %"         value={`${(t?.nullCount/ds.rows.length*100||0).toFixed(1)}%`} color={C.orange}/>
                    </div>
                  );
                })()}
              </div>
              <div style={{fontSize:9,color:C.t3,letterSpacing:'.12em',fontFamily:'JetBrains Mono,monospace',marginBottom:8}}>
                FEATURE COLUMNS — click to toggle on/off
              </div>
              <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:8}}>
                {ds.headers.filter(c=>c!==targetCol).map(col=>{
                  const t=types.find(t=>t.col===col);
                  const on=featureCols.includes(col);
                  const tc=t?.type==='numeric'?C.cyan:C.gold;
                  return(
                    <button key={col} onClick={()=>toggleFeat(col)} style={{
                      padding:'5px 11px',border:`1px solid ${on?tc:C.b1}`,
                      background:on?tc+'18':'transparent',color:on?tc:C.t4,
                      fontFamily:'JetBrains Mono,monospace',fontSize:10,borderRadius:3,cursor:'pointer',
                    }}>{on?'✓ ':''}{col} <span style={{fontSize:8,opacity:.6}}>{t?.type?.[0]}</span></button>
                  );
                })}
              </div>
              <div style={{fontSize:10,color:C.t3}}>
                {featureCols.length} features · {ds.rows.length} rows · {numCols.filter(c=>featureCols.includes(c)).length} numeric · {catCols.filter(c=>featureCols.includes(c)).length} categorical
              </div>
            </BlockBody>
          </Block>

          {/* Features preview based on current selection */}
          <MiniTable
            rows={ds.rows}
            headers={[
              ...(targetCol ? [targetCol] : []),
              ...featureCols,
            ]}
            title="FEATURES PREVIEW (TARGET + SELECTED FEATURES)"
            accent={C.cyan}
          />
        </div>
      )}

      {/* ── ENCODING ── */}
      {sub==='encoding'&&(
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          <Block>
            <BlockHead label="CATEGORICAL ENCODING" accent={C.violet}/>
            <BlockBody>
              <div style={{display:'flex',gap:12,flexWrap:'wrap',alignItems:'flex-end',marginBottom:14}}>
                <div style={{minWidth:260,flex:1}}>
                  <Label>CATEGORICAL COLUMNS</Label>
                  <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                    {(catCols.length?catCols:ds.headers).map(col=>{
                      const on=encodeCols.includes(col);
                      return (
                        <button
                          key={col}
                          onClick={()=>setEncodeCols(s=>s.includes(col)?s.filter(c=>c!==col):[...s,col])}
                          style={{
                            padding:'5px 12px',border:`1px solid ${on?C.violet:C.b1}`,
                            background:on?C.violet+'18':'transparent',color:on?C.violet:C.t3,
                            fontFamily:'JetBrains Mono,monospace',fontSize:10,borderRadius:3,cursor:'pointer',
                          }}
                        >
                          {on?'✓ ':''}{col}
                        </button>
                      );
                    })}
                    <button onClick={()=>setEncodeCols(catCols)} style={{padding:'5px 10px',border:`1px solid ${C.b1}`,background:'transparent',color:C.t4,fontFamily:'JetBrains Mono,monospace',fontSize:10,borderRadius:3,cursor:'pointer'}}>ALL</button>
                    <button onClick={()=>setEncodeCols([])} style={{padding:'5px 10px',border:`1px solid ${C.b1}`,background:'transparent',color:C.t4,fontFamily:'JetBrains Mono,monospace',fontSize:10,borderRadius:3,cursor:'pointer'}}>NONE</button>
                  </div>
                </div>
                <div>
                  <Label>METHOD</Label>
                  <div style={{display:'flex',gap:6}}>
                    {[['label','Label Encode'],['onehot','One-Hot (label used)']].map(([v,l])=>(
                      <button key={v} onClick={()=>setEncodeMethod(v)} style={{
                        padding:'7px 14px',border:`1px solid ${encodeMethod===v?C.violet:C.b1}`,
                        background:encodeMethod===v?C.violet+'18':'transparent',
                        color:encodeMethod===v?C.violet:C.t3,
                        fontFamily:'JetBrains Mono,monospace',fontSize:10,fontWeight:encodeMethod===v?700:400,
                        borderRadius:3,cursor:'pointer',
                      }}>{l}</button>
                    ))}
                  </div>
                </div>
                <Btn onClick={runEncoding} color={C.violet} variant="fill" disabled={!encodeCols.length}>ENCODE →</Btn>
              </div>

              {/* Method explainers */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:14}}>
                {[
                  {id:'label',name:'Label Encode',color:C.gold,formula:'cat → 0,1,2…',desc:'Maps each unique value to integer. Compact, no ordinal guarantee. Best for tree models.'},
                  {id:'onehot',name:'One-Hot Encode',color:C.cyan,formula:'cat → binary cols',desc:'One binary column per unique value. No ordinal. Best for linear models, NNs. Increases columns.'},
                ].map(m=>(
                  <div key={m.id} style={{padding:'10px 12px',background:C.raised,border:`1px solid ${encodeMethod===m.id?m.color+'44':C.b1}`,borderRadius:3,borderTop:`2px solid ${encodeMethod===m.id?m.color:C.b1}`}}>
                    <div style={{fontSize:10,color:m.color,fontFamily:'JetBrains Mono,monospace',fontWeight:700,marginBottom:2}}>{m.name}</div>
                    <div style={{fontSize:9,color:C.green,fontFamily:'JetBrains Mono,monospace',marginBottom:4}}>{m.formula}</div>
                    <div style={{fontSize:10,color:C.t3,lineHeight:1.6}}>{m.desc}</div>
                  </div>
                ))}
              </div>

              {/* Value map preview */}
              {encodeCols.length>0&&(()=>{
                const sampleCol = encodeCols[0];
                const uniq=[...new Set(ds.rows.map(r=>r[sampleCol]).filter(Boolean))].sort().slice(0,16);
                return(
                  <div>
                    <div style={{fontSize:9,color:C.t3,fontFamily:'JetBrains Mono,monospace',marginBottom:6}}>VALUE MAP SAMPLE — {sampleCol} ({uniq.length} unique values)</div>
                    <div style={{display:'flex',flexWrap:'wrap',gap:5}}>
                      {uniq.map((v,i)=>(
                        <div key={v} style={{padding:'3px 10px',background:C.raised,border:`1px solid ${C.b1}`,borderRadius:3,fontFamily:'JetBrains Mono,monospace',fontSize:10}}>
                          <span style={{color:C.t2}}>{v}</span>
                          <span style={{color:C.t4,margin:'0 5px'}}>→</span>
                          <span style={{color:C.violet,fontWeight:700}}>{i}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </BlockBody>
          </Block>

          {/* After: transformed preview first */}
          {encodeResult&&(
            <MiniTable
              rows={encodeResult.rows}
              headers={
                encodeResult.method==='label'
                  ? [
                      ...encodeResult.cols.flatMap(c=>[c,`${c}_enc`]),
                      ...ds.headers.filter(h=>!encodeResult.cols.includes(h)).slice(0,4),
                    ]
                  : [
                      ...encodeResult.cols,
                      ...Object.values(encodeResult.oneHotColsBySource || {}).flat().slice(0,8),
                    ]
              }
              title={`AFTER ENCODING — ${encodeResult.cols.length} column(s) · ${encodeResult.method.toUpperCase()}`}
              accent={C.violet}
            />
          )}

          {/* Before: original values */}
          {encodeCols.length>0&&(
            <MiniTable
              rows={ds.rows}
              headers={[...encodeCols, ...ds.headers.filter(h=>!encodeCols.includes(h)).slice(0,5)]}
              title="BEFORE ENCODING — original values"
              accent={C.orange}
            />
          )}

          {/* Stats change table */}
          {encodeResult&&encodeResult.method==='label'&&(()=>{
            const uniq=[...new Set(ds.rows.map(r=>r[encodeResult.cols[0]]).filter(Boolean))].sort();
            return(
              <Block>
                <BlockHead label={`ENCODING MAP — ${encodeResult.cols[0]}`} accent={C.violet}/>
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
                    <thead><tr style={{background:C.deep}}>
                      <th style={TH}>ORIGINAL VALUE</th>
                      <th style={{...TH,color:C.violet}}>ENCODED INTEGER</th>
                      <th style={TH}>FREQUENCY</th>
                      <th style={TH}>FREQ %</th>
                    </tr></thead>
                    <tbody>
                      {uniq.map((v,i)=>{
                        const cnt=ds.rows.filter(r=>r[encodeResult.cols[0]]===v).length;
                        return(
                          <tr key={v} style={{borderBottom:`1px solid ${C.b0}`,background:i%2===0?'transparent':C.raised+'80'}}>
                            <td style={{...TD,color:C.gold,fontWeight:700}}>{v}</td>
                            <td style={{...TD,color:C.violet,fontWeight:700}}>{i}</td>
                            <td style={{...TD,color:C.t2}}>{cnt}</td>
                            <td style={{...TD}}>
                              <div style={{display:'flex',alignItems:'center',gap:6}}>
                                <div style={{width:60,height:4,background:C.b1,borderRadius:2}}>
                                  <div style={{height:'100%',width:`${cnt/ds.rows.length*100}%`,background:C.violet,borderRadius:2}}/>
                                </div>
                                <span style={{color:C.t3}}>{(cnt/ds.rows.length*100).toFixed(1)}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Block>
            );
          })()}
        </div>
      )}

      {/* ── SCALING ── */}
      {sub==='scaling'&&(
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          <Block>
            <BlockHead label="FEATURE SCALING" accent={C.cyan}/>
            <BlockBody>
              <div style={{fontSize:11,color:C.t2,marginBottom:12,lineHeight:1.7}}>
                Normalize numeric features. Essential before KNN, SVM, gradient descent. Results in the preview tables below.
              </div>
              <div style={{fontSize:9,color:C.t3,fontFamily:'JetBrains Mono,monospace',letterSpacing:'.1em',marginBottom:8}}>SELECT COLUMNS TO SCALE</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:12}}>
                {numCols.map(col=>(
                  <button key={col} onClick={()=>setScaleCols(s=>s.includes(col)?s.filter(c=>c!==col):[...s,col])} style={{
                    padding:'5px 12px',border:`1px solid ${scaleCols.includes(col)?C.cyan:C.b1}`,
                    background:scaleCols.includes(col)?C.cyan+'18':'transparent',color:scaleCols.includes(col)?C.cyan:C.t3,
                    fontFamily:'JetBrains Mono,monospace',fontSize:10,borderRadius:3,cursor:'pointer',
                  }}>{col}</button>
                ))}
                <button onClick={()=>setScaleCols(numCols)} style={{padding:'5px 10px',border:`1px solid ${C.b1}`,background:'transparent',color:C.t4,fontFamily:'JetBrains Mono,monospace',fontSize:10,borderRadius:3,cursor:'pointer'}}>ALL</button>
                <button onClick={()=>setScaleCols([])} style={{padding:'5px 10px',border:`1px solid ${C.b1}`,background:'transparent',color:C.t4,fontFamily:'JetBrains Mono,monospace',fontSize:10,borderRadius:3,cursor:'pointer'}}>NONE</button>
              </div>
              <div style={{display:'flex',gap:8,marginBottom:14,flexWrap:'wrap',alignItems:'flex-end'}}>
                <div>
                  <Label>METHOD</Label>
                  <div style={{display:'flex',gap:6}}>
                    {[['standard','Z-Score (μ=0,σ=1)'],['minmax','Min-Max [0,1]']].map(([v,l])=>(
                      <button key={v} onClick={()=>setScaleMethod(v)} style={{
                        padding:'7px 14px',border:`1px solid ${scaleMethod===v?C.cyan:C.b1}`,
                        background:scaleMethod===v?C.cyan+'18':'transparent',
                        color:scaleMethod===v?C.cyan:C.t3,
                        fontFamily:'JetBrains Mono,monospace',fontSize:10,fontWeight:scaleMethod===v?700:400,
                        borderRadius:3,cursor:'pointer',
                      }}>{l}</button>
                    ))}
                  </div>
                </div>
                <Btn onClick={runScaling} color={C.cyan} variant="fill" disabled={!scaleCols.length}>SCALE COLUMNS →</Btn>
              </div>

              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                {[
                  {name:'StandardScaler',formula:'x′ = (x − μ) / σ',range:'∈ N(0,1)',pros:'Handles outliers, good for PCA/regression',use:'Linear reg, Logistic reg, SVM, PCA'},
                  {name:'MinMaxScaler',formula:'x′ = (x − min)/(max − min)',range:'[0, 1]',pros:'Bounded, good for NNs',use:'KNN, neural nets, distance-based'},
                ].map(m=>(
                  <div key={m.name} style={{padding:'10px 12px',background:C.raised,border:`1px solid ${scaleMethod===(m.name==='StandardScaler'?'standard':'minmax')?C.cyan+'44':C.b1}`,borderRadius:3,borderTop:`2px solid ${C.cyan}`}}>
                    <div style={{fontSize:10,color:C.cyan,fontFamily:'JetBrains Mono,monospace',fontWeight:700,marginBottom:2}}>{m.name}</div>
                    <div style={{fontSize:10,color:C.green,fontFamily:'JetBrains Mono,monospace',marginBottom:4}}>{m.formula} → {m.range}</div>
                    <div style={{fontSize:9,color:C.t3,lineHeight:1.5}}>Pros: {m.pros}<br/><span style={{color:C.cyan}}>Use for:</span> {m.use}</div>
                  </div>
                ))}
              </div>
            </BlockBody>
          </Block>

          {/* After: transformed preview first */}
          {scaleResult&&(
            <MiniTable
              rows={scaleResult.rows}
              headers={scaleResult.cols.flatMap(c=>[c,`${c}ˢ`]).slice(0,10)}
              title={`AFTER SCALING — original (col) vs scaled (colˢ) · method: ${scaleResult.method}`}
              accent={C.cyan}
            />
          )}

          {/* Before: original numeric cols */}
          {scaleCols.length>0&&(
            <MiniTable
              rows={ds.rows}
              headers={scaleCols.slice(0,8)}
              title="BEFORE SCALING — original values"
              accent={C.orange}
            />
          )}

          {scaleResult&&(
            <>
              {/* Stats comparison table */}
              <Block>
                <BlockHead label="BEFORE / AFTER STATS — ALL SCALED COLUMNS" accent={C.cyan}/>
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:10}}>
                    <thead>
                      <tr style={{background:C.deep}}>
                        <th style={TH}>COLUMN</th>
                        <th colSpan={4} style={{...TH,textAlign:'center',color:C.orange,borderLeft:`1px solid ${C.b0}`}}>ORIGINAL</th>
                        <th colSpan={4} style={{...TH,textAlign:'center',color:C.cyan,borderLeft:`1px solid ${C.b0}`}}>SCALED</th>
                      </tr>
                      <tr style={{background:C.raised}}>
                        <th style={TH}></th>
                        {['Min','Max','Mean','Std','Min','Max','Mean','Std'].map((h,i)=>(
                          <th key={i} style={{...TH,textAlign:'right',color:i<4?C.orange:C.cyan,borderLeft:i===4?`1px solid ${C.b0}`:'none'}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {scaleResult.statsTable.map((s,i)=>(
                        <tr key={s.col} style={{borderBottom:`1px solid ${C.b0}`,background:i%2===0?'transparent':C.raised+'80'}}>
                          <td style={{...TD,color:C.t1,fontWeight:700}}>{s.col}</td>
                          <td style={{...TD,textAlign:'right',color:C.orange}}>{s.origMin}</td>
                          <td style={{...TD,textAlign:'right',color:C.orange}}>{s.origMax}</td>
                          <td style={{...TD,textAlign:'right',color:C.orange}}>{s.origMean}</td>
                          <td style={{...TD,textAlign:'right',color:C.orange}}>{s.origStd}</td>
                          <td style={{...TD,textAlign:'right',color:C.cyan,fontWeight:700,borderLeft:`1px solid ${C.b0}`}}>{s.scaledMin}</td>
                          <td style={{...TD,textAlign:'right',color:C.cyan,fontWeight:700}}>{s.scaledMax}</td>
                          <td style={{...TD,textAlign:'right',color:C.cyan,fontWeight:700}}>{s.scaledMean}</td>
                          <td style={{...TD,textAlign:'right',color:C.cyan,fontWeight:700}}>{s.scaledStd}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Block>
            </>
          )}
        </div>
      )}

      {/* ── FEATURE ANALYSIS ── */}
      {sub==='analysis'&&(
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          {!targetCol&&(
            <div style={{background:C.orange+'12',border:`1px solid ${C.orange}33`,borderRadius:4,padding:14,color:C.orange,fontSize:11}}>
              ⚠ Select a target column in the TARGET tab first.
            </div>
          )}
          {targetCol&&(
            <>
              <Block>
                <BlockHead label="FEATURE IMPORTANCE W.R.T. TARGET" accent={C.green}
                  right={<PinButton chart={{id:'feat_importance',type:'bar',title:'Feature Importance',subtitle:`vs ${targetCol}`}} small/>}
                />
                <BlockBody>
                  <div style={{fontSize:10,color:C.t3,marginBottom:12}}>
                    {taskType==='regression'?'Pearson |correlation| with target':'ANOVA ratio: between-group / total variance'}
                  </div>
                  <ResponsiveContainer width="100%" height={Math.max(160,featImportance.length*26+30)}>
                    <BarChart data={featImportance} layout="vertical" barCategoryGap="18%" margin={{top:0,right:70,bottom:0,left:0}}>
                      <CartesianGrid strokeDasharray="2 2" stroke={C.b0} horizontal={false}/>
                      <XAxis type="number" domain={[0,1]} tick={{fill:C.t4,fontSize:8}}/>
                      <YAxis dataKey="col" type="category" tick={{fill:C.t2,fontSize:9}} width={130}/>
                      <Tooltip content={<ChartTip/>}/>
                      <Bar dataKey="imp" name="importance" radius={[0,3,3,0]}>
                        {featImportance.map((_,i)=><Cell key={i} fill={PALETTE[i%PALETTE.length]}/>)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </BlockBody>
              </Block>

              <Block>
                <BlockHead label="FULL FEATURE SUMMARY TABLE" accent={C.cyan}/>
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
                    <thead><tr style={{background:C.deep}}>
                      {['#','FEATURE','TYPE','UNIQUE','NULL%','IMPORTANCE','SIGNAL','μ','σ'].map(h=><th key={h} style={TH}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {featImportance.map((f,i)=>{
                        const t=types.find(t=>t.col===f.col);
                        const np=(t?.nullCount/ds.rows.length*100||0).toFixed(1);
                        const sig=+np>40?'DROP':f.imp<0.03?'LOW SIGNAL':f.imp>0.5?'HIGH VALUE':'KEEP';
                        const sc=sig==='DROP'?C.red:sig==='LOW SIGNAL'?C.gold:sig==='HIGH VALUE'?C.green:C.t3;
                        const s=f.type==='numeric'?colStats(ds.rows,f.col):null;
                        return(
                          <tr key={f.col} style={{borderBottom:`1px solid ${C.b0}`,background:i%2===0?'transparent':C.raised+'80'}}>
                            <td style={{...TD,color:C.t4}}>{i+1}</td>
                            <td style={{...TD,color:C.t1,fontWeight:700,borderLeft:`2px solid ${PALETTE[i%PALETTE.length]}`}}>{f.col}</td>
                            <td style={{...TD}}><span style={{fontSize:9,padding:'1px 6px',borderRadius:2,background:(f.type==='numeric'?C.cyan:C.gold)+'18',color:f.type==='numeric'?C.cyan:C.gold,border:`1px solid ${f.type==='numeric'?C.cyan:C.gold}33`}}>{f.type}</span></td>
                            <td style={{...TD,color:C.t2}}>{t?.uniqueCount}</td>
                            <td style={{...TD,color:+np>20?C.red:C.t3}}>{np}%</td>
                            <td style={{padding:'6px 11px'}}>
                              <div style={{display:'flex',alignItems:'center',gap:6}}>
                                <div style={{width:60,height:5,background:C.b1,borderRadius:2}}>
                                  <div style={{height:'100%',width:`${Math.min(f.imp*100,100)}%`,background:PALETTE[i%PALETTE.length],borderRadius:2}}/>
                                </div>
                                <span style={{fontFamily:'JetBrains Mono,monospace',fontSize:9,color:C.t2}}>{(f.imp*100).toFixed(1)}%</span>
                              </div>
                            </td>
                            <td style={{...TD,color:sc,fontWeight:700}}>{sig}</td>
                            <td style={{...TD,color:C.t3}}>{s?s.mean:'—'}</td>
                            <td style={{...TD,color:C.t3}}>{s?s.std:'—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Block>
            </>
          )}
        </div>
      )}

      {/* ── PCA ── */}
      {sub==='pca'&&(
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          <Block>
            <BlockHead label="PRINCIPAL COMPONENT ANALYSIS" accent={C.pink}/>
            <BlockBody>
              <div style={{fontSize:11,color:C.t2,marginBottom:12,lineHeight:1.7}}>
                Select 2+ numeric features. PCA extracts orthogonal axes that maximize variance — visualization, noise reduction, feature compression.
              </div>
              <div style={{fontSize:9,color:C.t3,fontFamily:'JetBrains Mono,monospace',letterSpacing:'.1em',marginBottom:8}}>SELECT FEATURES</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:12}}>
                {numCols.map(col=>(
                  <button key={col} onClick={()=>setPcaCols(s=>s.includes(col)?s.filter(c=>c!==col):[...s,col])} style={{
                    padding:'5px 12px',border:`1px solid ${pcaCols.includes(col)?C.pink:C.b1}`,
                    background:pcaCols.includes(col)?C.pink+'18':'transparent',color:pcaCols.includes(col)?C.pink:C.t3,
                    fontFamily:'JetBrains Mono,monospace',fontSize:10,borderRadius:3,cursor:'pointer',
                  }}>{col}</button>
                ))}
              </div>
              <div style={{display:'flex',gap:12,alignItems:'center',marginBottom:4}}>
                <div>
                  <Label>N COMPONENTS</Label>
                  <select value={pcaK} onChange={e=>setPcaK(+e.target.value)} style={{background:C.deep,border:`1px solid ${C.b2}`,color:C.t1,padding:'6px 10px',fontSize:11,fontFamily:'JetBrains Mono,monospace',borderRadius:3,cursor:'pointer',outline:'none'}}>
                    {[2,3,4,5,6,8].map(k=><option key={k} value={k}>{k}</option>)}
                  </select>
                </div>
                <div style={{paddingTop:14}}>
                  <Btn onClick={runPCA} color={C.pink} variant="fill" disabled={pcaCols.length<2}>RUN PCA →</Btn>
                </div>
              </div>
            </BlockBody>
          </Block>

          {pcaResult&&(
            <>
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
                <MBadge label="PC1 VARIANCE"  value={`${pcaResult.components[0]?.variancePct}%`}  color={C.cyan}/>
                <MBadge label="PC2 VARIANCE"  value={`${pcaResult.components[1]?.variancePct||0}%`} color={C.green}/>
                <MBadge label="2D TOTAL"       value={`${((pcaResult.components[0]?.variancePct||0)+(pcaResult.components[1]?.variancePct||0)).toFixed(1)}%`} color={C.violet}/>
                <MBadge label="FEATURES USED" value={pcaCols.length}                               color={C.t2}/>
              </div>

              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                <Block>
                  <BlockHead label="SCREE PLOT" accent={C.violet}
                    right={<PinButton chart={{id:'pca_scree',type:'bar',title:'PCA Scree Plot',subtitle:'variance per component'}} small/>}
                  />
                  <BlockBody>
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={pcaResult.components.map(c=>({name:c.name,variance:c.variancePct}))} barCategoryGap="20%">
                        <CartesianGrid strokeDasharray="2 2" stroke={C.b0}/>
                        <XAxis dataKey="name" tick={{fill:C.t3,fontSize:9}}/>
                        <YAxis tick={{fill:C.t4,fontSize:8}} unit="%"/>
                        <Tooltip content={<ChartTip/>}/>
                        <Bar dataKey="variance" name="Variance %" radius={[3,3,0,0]}>
                          {pcaResult.components.map((_,i)=><Cell key={i} fill={PALETTE[i%PALETTE.length]}/>)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    <div style={{fontSize:9,color:C.t3,marginTop:6}}>◈ Look for the "elbow" — after it is noise.</div>
                  </BlockBody>
                </Block>

                <Block>
                  <BlockHead label="PC1 vs PC2 SCATTER" accent={C.cyan}
                    right={<PinButton chart={{id:'pca_scatter',type:'scatter',xCol:'PC1',yCol:'PC2',title:'PCA Projection PC1 vs PC2'}} small/>}
                  />
                  <BlockBody>
                    <ResponsiveContainer width="100%" height={180}>
                      <ScatterChart margin={{top:5,right:10,bottom:20,left:0}}>
                        <CartesianGrid strokeDasharray="2 2" stroke={C.b0}/>
                        <XAxis dataKey="x" name="PC1" type="number" tick={{fill:C.t4,fontSize:8}}
                          label={{value:`PC1 (${pcaResult.components[0]?.variancePct}%)`,position:'insideBottom',offset:-5,fill:C.t3,fontSize:9}}/>
                        <YAxis dataKey="y" name="PC2" type="number" tick={{fill:C.t4,fontSize:8}}/>
                        <Tooltip content={<ChartTip/>}/>
                        <Scatter data={pcaResult.projections.slice(0,400).map(p=>({x:+p[0].toFixed(3),y:+(p[1]||0).toFixed(3)}))} fill={C.cyan} opacity={0.55} r={3}/>
                      </ScatterChart>
                    </ResponsiveContainer>
                  </BlockBody>
                </Block>
              </div>

              <Block>
                <BlockHead label="PC1 & PC2 FEATURE LOADINGS" accent={C.green}
                  right={<PinButton chart={{id:'pca_loadings',type:'bar',title:'PCA Feature Loadings'}} small/>}
                />
                <BlockBody>
                  <ResponsiveContainer width="100%" height={Math.max(140,pcaCols.length*30+40)}>
                    <BarChart
                      data={pcaResult.components[0].loadings.map((_,fi)=>({
                        col:pcaResult.featureNames[fi]||`f${fi}`,
                        pc1:+pcaResult.components[0].loadings[fi].loading.toFixed(4),
                        pc2:+(pcaResult.components[1]?.loadings[fi]?.loading||0).toFixed(4),
                      }))}
                      layout="vertical" barCategoryGap="18%" margin={{top:0,right:20,bottom:0,left:0}}
                    >
                      <CartesianGrid strokeDasharray="2 2" stroke={C.b0} horizontal={false}/>
                      <XAxis type="number" domain={[-1,1]} tick={{fill:C.t4,fontSize:8}}/>
                      <YAxis dataKey="col" type="category" tick={{fill:C.t2,fontSize:9}} width={120}/>
                      <Tooltip content={<ChartTip/>}/>
                      <Legend wrapperStyle={{fontSize:10,color:C.t2}}/>
                      <Bar dataKey="pc1" name="PC1" fill={C.cyan} opacity={0.85} radius={[0,2,2,0]}/>
                      <Bar dataKey="pc2" name="PC2" fill={C.green} opacity={0.85} radius={[0,2,2,0]}/>
                    </BarChart>
                  </ResponsiveContainer>
                </BlockBody>
              </Block>

              {/* Eigenvalue + loading table */}
              <Block>
                <BlockHead label="EIGENVALUES & CUMULATIVE VARIANCE" accent={C.violet}/>
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
                    <thead><tr style={{background:C.deep}}>
                      {['COMPONENT','EIGENVALUE','VARIANCE %','CUMULATIVE %','STATUS'].map(h=><th key={h} style={TH}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {pcaResult.components.reduce((acc,c,i)=>{
                        const cum=+(i===0?c.variancePct:(acc[i-1]?.cum||0)+c.variancePct).toFixed(2);
                        acc.push({...c,cum}); return acc;
                      },[]).map((c,i)=>(
                        <tr key={c.name} style={{borderBottom:`1px solid ${C.b0}`}}>
                          <td style={{...TD,color:PALETTE[i%PALETTE.length],fontWeight:700}}>{c.name}</td>
                          <td style={{...TD,color:C.t1}}>{c.eigenvalue}</td>
                          <td style={{padding:'6px 11px'}}>
                            <div style={{display:'flex',alignItems:'center',gap:8}}>
                              <div style={{width:70,height:5,background:C.b1,borderRadius:2}}>
                                <div style={{height:'100%',width:`${c.variancePct}%`,background:PALETTE[i%PALETTE.length],borderRadius:2}}/>
                              </div>
                              <span style={{fontFamily:'JetBrains Mono,monospace',fontSize:10,color:C.t1}}>{c.variancePct}%</span>
                            </div>
                          </td>
                          <td style={{...TD,color:c.cum>=90?C.green:C.t2,fontWeight:c.cum>=90?700:400}}>{c.cum}%</td>
                          <td style={{...TD,color:c.cum>=95?C.green:c.cum>=80?C.gold:C.t3}}>
                            {c.cum>=95?'✓ SUFFICIENT':c.cum>=80?'GOOD':'NEEDS MORE'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Block>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TRAINING TAB — with feature matrix preview table
// ─────────────────────────────────────────────────────────────────────────────
function TrainingTab({ ds, config, results, onResultsChange }) {
  const { targetCol='', featureCols=[], encodings={}, scalingMethod={} } = config;
  const [selected,   setSelected]   = useState([]);
  const [running,    setRunning]    = useState(false);
  const [progress,   setProgress]   = useState({});
  const [trainLog,   setTrainLog]   = useState([]);
  const [testRatio,  setTestRatio]  = useState(0.2);
  const [matrixRows, setMatrixRows] = useState(null); // feature matrix preview
  const types = useMemo(()=>inferTypes(ds.headers,ds.rows),[ds]);

  const taskType = useMemo(()=>{
    if(!targetCol) return null;
    const t=types.find(t=>t.col===targetCol);
    const vals=ds.rows.map(r=>r[targetCol]).filter(Boolean);
    const uniq=new Set(vals).size;
    return (t?.type==='categorical'||(uniq<=20&&vals.every(v=>Number.isInteger(parseFloat(v)))))?'classification':'regression';
  },[targetCol,types,ds]);

  const algos        = taskType==='classification'?CLF_ALGOS:REG_ALGOS;
  const primaryMetric= taskType==='classification'?'accuracy':'r2';
  const toggleAlgo   = id => setSelected(s=>s.includes(id)?s.filter(x=>x!==id):[...s,id]);
  const markTrainLog = useCallback((algoId, status) => {
    setTrainLog((prev) => prev.map((entry) => entry.algoId === algoId ? { ...entry, status, at: new Date().toLocaleTimeString() } : entry));
  }, []);

  // Build and show the feature matrix preview
  const buildPreview = useCallback(()=>{
    if(!targetCol||featureCols.length===0) return;
    try {
      const mat=buildMatrix(ds,featureCols,targetCol,encodings,scalingMethod);
      const featureNames=['(bias)',...featureCols];
      const previewRows=mat.X.slice(0,50).map((row,i)=>{
        const obj={[`y_${targetCol}`]:mat.y[i]};
        row.forEach((v,j)=>{ obj[featureNames[j]||`f${j}`]=typeof v==='number'?+v.toFixed(4):v; });
        return obj;
      });
      setMatrixRows({ rows:previewRows, headers:[`y_${targetCol}`,...featureNames], n:mat.X.length, p:featureNames.length });
    } catch(e){ console.warn(e); }
  },[ds,config]);

  const runTraining = useCallback(async()=>{
    if(!targetCol||featureCols.length===0||selected.length===0) return;
    setRunning(true); setProgress({});
    const selectedAlgos = selected
      .map((algoId) => algos.find((algo) => algo.id === algoId))
      .filter(Boolean);
    setTrainLog(selectedAlgos.map((algo) => ({ algoId: algo.id, algoName: algo.name, status: 'queued', at: '' })));
    let matrix;
    try { matrix=buildMatrix(ds,featureCols,targetCol,encodings,scalingMethod); }
    catch(e){ setRunning(false); return; }
    const {X,y,isClassification}=matrix;
    const {Xtr,ytr,Xte,yte}=trainTestSplit(X,y,testRatio);
    const newResults={...results};
    for(const algoId of selected){
      const algo=algos.find(a=>a.id===algoId); if(!algo) continue;
      setProgress(p=>({...p,[algoId]:'running'}));
      markTrainLog(algoId, 'running');
      await new Promise(r=>setTimeout(r,10));
      try{
        const ypred=algo.run(Xtr,ytr,Xte);
        const metrics=isClassification?classMetrics(yte,ypred):regressionMetrics(yte,ypred);
        const confusion = isClassification ? computeConfusionMatrix(yte, ypred) : null;
        newResults[algoId]={algoId,algoName:algo.name,color:algo.color,metrics,ypred,yte,
          taskType:isClassification?'classification':'regression',
          trainedAt:new Date().toISOString(),nTrain:ytr.length,nTest:yte.length, confusion};
        setProgress(p=>({...p,[algoId]:'done'}));
        markTrainLog(algoId, 'done');
      }catch(e){ setProgress(p=>({...p,[algoId]:'error'})); markTrainLog(algoId, 'error'); }
      onResultsChange({...newResults});
    }
    setRunning(false);
  },[ds,config,selected,algos,testRatio,results,onResultsChange,markTrainLog]);

  return (
    <div style={{display:'flex',flexDirection:'column',gap:16,position:'relative'}}>
      {running && trainLog.length > 0 && (
        <div style={{
          position:'fixed', inset:0, zIndex:12000,
          background:'rgba(2,5,8,.66)', display:'flex', alignItems:'center', justifyContent:'center',
        }}>
          <div style={{ width:520, maxWidth:'94vw', background:C.surface, border:`1px solid ${C.b2}`, borderRadius:6, overflow:'hidden', boxShadow:'0 28px 70px rgba(0,0,0,.8)' }}>
            <div style={{ padding:'11px 14px', background:C.deep, borderBottom:`1px solid ${C.b0}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:10, color:C.orange, letterSpacing:'.12em', fontWeight:700, fontFamily:'JetBrains Mono,monospace' }}>MODEL TRAINING STATUS</span>
              <span style={{ fontSize:9, color:C.t4, fontFamily:'JetBrains Mono,monospace' }}>
                {trainLog.filter(t=>t.status==='done').length}/{trainLog.length} done
              </span>
            </div>
            <div style={{ padding:14, display:'flex', flexDirection:'column', gap:8, maxHeight:'62vh', overflowY:'auto' }}>
              {trainLog.map((entry, idx) => {
                const color = entry.status==='done' ? C.green : entry.status==='running' ? C.gold : entry.status==='error' ? C.red : C.t3;
                const icon = entry.status==='done' ? '✓' : entry.status==='running' ? '⟳' : entry.status==='error' ? '✗' : '…';
                return (
                  <div key={entry.algoId} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 10px', border:`1px solid ${color}55`, background:`${color}12`, borderRadius:4 }}>
                    <span style={{ width:22, textAlign:'center', color, fontWeight:700, fontFamily:'JetBrains Mono,monospace' }}>{icon}</span>
                    <span style={{ color:C.t4, fontSize:9, width:24, fontFamily:'JetBrains Mono,monospace' }}>{idx + 1}.</span>
                    <span style={{ flex:1, color:C.t2, fontSize:10, fontFamily:'JetBrains Mono,monospace' }}>{entry.algoName}</span>
                    <span style={{ color, fontSize:9, fontFamily:'JetBrains Mono,monospace', fontWeight:700, minWidth:70, textAlign:'right' }}>{entry.status.toUpperCase()}</span>
                    <span style={{ color:C.t4, fontSize:8, fontFamily:'JetBrains Mono,monospace', minWidth:64, textAlign:'right' }}>{entry.at || ''}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {!targetCol&&(
        <div style={{background:C.orange+'12',border:`1px solid ${C.orange}44`,borderRadius:4,padding:14,color:C.orange,fontSize:11}}>
          ⚠ Go to FEATURES → TARGET and select a target column first.
        </div>
      )}

      {taskType&&(
        <div style={{display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
          <div style={{padding:'6px 14px',background:taskType==='regression'?C.cyan+'18':C.green+'18',border:`1px solid ${taskType==='regression'?C.cyan:C.green}44`,borderRadius:3}}>
            <span style={{fontFamily:'JetBrains Mono,monospace',fontSize:10,fontWeight:700,color:taskType==='regression'?C.cyan:C.green}}>
              {taskType==='regression'?'📈 REGRESSION':'🏷 CLASSIFICATION'} · Target: <strong>{targetCol}</strong> · {featureCols.length} features
            </span>
          </div>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <Label>TEST SPLIT</Label>
            <select value={testRatio} onChange={e=>setTestRatio(+e.target.value)} style={{background:C.deep,border:`1px solid ${C.b2}`,color:C.t1,padding:'5px 8px',fontSize:10,fontFamily:'JetBrains Mono,monospace',borderRadius:3,cursor:'pointer',outline:'none'}}>
              {[0.1,0.15,0.2,0.25,0.3].map(v=><option key={v} value={v}>{Math.round(v*100)}%</option>)}
            </select>
          </div>
        </div>
      )}

      {/* Feature matrix preview */}
      {targetCol&&featureCols.length>0&&(
        <Block>
          <BlockHead label="TRAINING MATRIX PREVIEW — what goes into models" accent={C.green}
            right={<Btn small onClick={buildPreview} color={C.green}>BUILD PREVIEW</Btn>}
          />
          {matrixRows?(
            <div style={{display:'flex',flexDirection:'column',gap:0}}>
              <div style={{padding:'8px 14px',background:C.raised,borderBottom:`1px solid ${C.b0}`,display:'flex',gap:20,flexWrap:'wrap'}}>
                <span style={{fontSize:9,color:C.t3,fontFamily:'JetBrains Mono,monospace'}}>MATRIX SHAPE: <span style={{color:C.green}}>{matrixRows.n} × {matrixRows.p}</span></span>
                <span style={{fontSize:9,color:C.t3,fontFamily:'JetBrains Mono,monospace'}}>TRAIN: <span style={{color:C.cyan}}>{Math.round(matrixRows.n*(1-testRatio))}</span></span>
                <span style={{fontSize:9,color:C.t3,fontFamily:'JetBrains Mono,monospace'}}>TEST: <span style={{color:C.orange}}>{Math.round(matrixRows.n*testRatio)}</span></span>
                <span style={{fontSize:9,color:C.t3,fontFamily:'JetBrains Mono,monospace'}}>TARGET: <span style={{color:taskType==='regression'?C.cyan:C.green}}>{targetCol}</span></span>
              </div>
              <MiniTable rows={matrixRows.rows} headers={matrixRows.headers.slice(0,10)} title="" accent={C.green} showModeBar={true}/>
              {matrixRows.headers.length>10&&<div style={{padding:'5px 14px',fontSize:9,color:C.t4,fontFamily:'JetBrains Mono,monospace'}}>…showing 10 of {matrixRows.headers.length} columns</div>}
            </div>
          ):(
            <BlockBody>
              <div style={{fontSize:11,color:C.t3,marginBottom:10}}>
                Shows the exact numerical matrix (encoded + scaled features + target) that will be fed to each algorithm. Bias column is added automatically.
              </div>
              <Btn onClick={buildPreview} color={C.green}>BUILD MATRIX PREVIEW →</Btn>
            </BlockBody>
          )}
        </Block>
      )}

      <Block>
        <BlockHead label={`TOP 10 ${taskType==='classification'?'CLASSIFIERS':'REGRESSORS'} — select to train`} accent={C.orange}
          right={<div style={{display:'flex',gap:6}}>
            <Btn small onClick={()=>setSelected(algos.map(a=>a.id))} color={C.cyan}>ALL</Btn>
            <Btn small onClick={()=>setSelected([])} color={C.t3}>CLEAR</Btn>
          </div>}
        />
        <BlockBody>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))',gap:8}}>
            {algos.map(algo=>{
              const on=selected.includes(algo.id);
              const res=results[algo.id];
              const prog=progress[algo.id];
              return(
                <div key={algo.id} onClick={()=>!running&&toggleAlgo(algo.id)} style={{
                  display:'flex',alignItems:'center',gap:10,padding:'10px 12px',
                  background:on?algo.color+'12':C.raised,border:`2px solid ${on?algo.color:C.b1}`,
                  borderRadius:4,cursor:running?'wait':'pointer',transition:'all .15s',
                }}>
                  <div style={{width:10,height:10,borderRadius:'50%',background:on?algo.color:C.b2,flexShrink:0,border:`2px solid ${on?algo.color:C.b2}`}}/>
                  <div style={{flex:1}}>
                    <div style={{fontSize:10,color:on?algo.color:C.t2,fontFamily:'JetBrains Mono,monospace',fontWeight:on?700:400}}>{algo.name}</div>
                    {res&&<div style={{fontSize:9,color:C.t3,marginTop:2}}>
                      {taskType==='classification'?`Acc: ${(res.metrics.accuracy*100).toFixed(1)}%  F1: ${(res.metrics.f1*100).toFixed(1)}%`:`R²: ${res.metrics.r2}  RMSE: ${res.metrics.rmse}`}
                    </div>}
                  </div>
                  <span style={{fontSize:12,flexShrink:0}}>
                    {prog==='running'&&<span style={{color:C.gold,animation:'blink 1s infinite'}}>⟳</span>}
                    {prog==='done'&&<span style={{color:C.green}}>✓</span>}
                    {prog==='error'&&<span style={{color:C.red}}>✗</span>}
                  </span>
                </div>
              );
            })}
          </div>
        </BlockBody>
      </Block>

      <div style={{display:'flex',gap:12,alignItems:'center'}}>
        <Btn onClick={runTraining} color={C.orange} variant="fill" disabled={running||selected.length===0||!targetCol}>
          {running?'⟳ TRAINING…':`▶ TRAIN ${selected.length} MODEL${selected.length!==1?'S':''}`}
        </Btn>
        {selected.length===0&&<span style={{fontSize:10,color:C.t4}}>Select at least one algorithm</span>}
      </div>

      {/* Results */}
      {Object.values(results).length>0&&(
        <>
          {/* Summary leaderboard table */}
          <Block>
            <BlockHead label="RESULTS LEADERBOARD" accent={C.green}/>
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
                <thead><tr style={{background:C.deep}}>
                  <th style={TH}>#</th><th style={TH}>MODEL</th>
                  {Object.keys(Object.values(results)[0].metrics).map(m=><th key={m} style={{...TH,color:C.green}}>{m.toUpperCase()}</th>)}
                  <th style={TH}>TRAIN N</th><th style={TH}>TEST N</th><th style={TH}>TRAINED AT</th>
                </tr></thead>
                <tbody>
                  {Object.values(results).sort((a,b)=>b.metrics[primaryMetric]-a.metrics[primaryMetric]).map((res,i)=>(
                    <tr key={res.algoId} style={{borderBottom:`1px solid ${C.b0}`,background:i===0?res.color+'08':'transparent'}}>
                      <td style={{...TD,color:i===0?C.gold:C.t4,fontWeight:700}}>{i===0?'🏆':i+1}</td>
                      <td style={{...TD,color:res.color,fontWeight:700,borderLeft:`2px solid ${res.color}`}}>{res.algoName}</td>
                      {Object.entries(res.metrics).map(([k,v])=>{
                        const isBest=Math.abs(v-Object.values(results).sort((a,b)=>b.metrics[k]-a.metrics[k])[0].metrics[k])<1e-6;
                        return<td key={k} style={{...TD,color:isBest?C.green:C.t2,fontWeight:isBest?700:400}}>
                          {taskType==='classification'?`${(v*100).toFixed(1)}%`:v}
                        </td>;
                      })}
                      <td style={{...TD,color:C.t3}}>{res.nTrain}</td>
                      <td style={{...TD,color:C.t3}}>{res.nTest}</td>
                      <td style={{...TD,color:C.t4,fontSize:9}}>{new Date(res.trainedAt).toLocaleTimeString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Block>

          {/* Metric cards */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:10}}>
            {Object.values(results).sort((a,b)=>b.metrics[primaryMetric]-a.metrics[primaryMetric]).map((res,i)=>(
              <div key={res.algoId} style={{background:C.surface,border:`2px solid ${i===0?res.color:C.b1}`,borderRadius:4,overflow:'hidden',position:'relative'}}>
                {i===0&&<div style={{position:'absolute',top:8,right:36,fontSize:9,color:res.color,fontFamily:'JetBrains Mono,monospace',fontWeight:700}}>🏆</div>}
                <div style={{position:'absolute',top:7,right:8}}>
                  <PinButton chart={{id:`train_${res.algoId}`,type:'bar',title:`${res.algoName} Metrics`,subtitle:res.taskType}} small/>
                </div>
                <div style={{padding:'9px 14px',background:C.deep,borderBottom:`1px solid ${C.b0}`,borderLeft:`3px solid ${res.color}`}}>
                  <div style={{fontSize:10,color:res.color,fontFamily:'JetBrains Mono,monospace',fontWeight:700}}>{res.algoName}</div>
                  <div style={{fontSize:9,color:C.t4,marginTop:2}}>{res.taskType} · Train:{res.nTrain} · Test:{res.nTest}</div>
                </div>
                <div style={{padding:12,display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                  {Object.entries(res.metrics).map(([k,v])=>(
                    <MBadge key={k} label={k.toUpperCase()} value={typeof v==='number'?(k==='accuracy'||k==='f1'||k==='precision'||k==='recall'?`${(v*100).toFixed(1)}%`:v):v} color={res.color}/>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {taskType==='classification'&&(
            <Block>
              <BlockHead label="CONFUSION MATRICES — ALL CLASSIFIERS" accent={C.violet}/>
              <BlockBody>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))',gap:10}}>
                  {Object.values(results)
                    .sort((a,b)=>b.metrics[primaryMetric]-a.metrics[primaryMetric])
                    .map((res)=>
                      res.confusion ? (
                        <ConfusionMatrixView
                          key={`cm-${res.algoId}`}
                          confusion={res.confusion}
                          color={res.color}
                          title={`${res.algoName} — CONFUSION MATRIX`}
                        />
                      ) : null
                    )}
                </div>
              </BlockBody>
            </Block>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPARE TAB
// ─────────────────────────────────────────────────────────────────────────────
function CompareTab({ results }) {
  const [view, setView] = useState('table');
  const models = Object.values(results);

  if(models.length===0) return(
    <div style={{textAlign:'center',padding:'60px 20px',color:C.t4}}>
      <div style={{fontSize:36,marginBottom:12,opacity:0.3}}>⬡</div>
      <div style={{fontFamily:'Syne,sans-serif',fontSize:15,color:C.t3}}>No models trained yet</div>
      <div style={{fontSize:11,marginTop:6}}>Go to TRAIN tab and run some algorithms first.</div>
    </div>
  );

  const taskType=models[0].taskType;
  const metrics=Object.keys(models[0].metrics);
  const primaryKey=taskType==='classification'?'accuracy':'r2';
  const sorted=[...models].sort((a,b)=>b.metrics[primaryKey]-a.metrics[primaryKey]);

  return(
    <div style={{display:'flex',flexDirection:'column',gap:16}}>
      <div style={{display:'flex',gap:4}}>
        {[
          {id:'table',l:'TABLE'},
          {id:'bar',l:'BAR CHARTS'},
          {id:'radar',l:'RADAR'},
          ...(taskType==='classification' ? [{id:'confusion',l:'CONFUSION'}] : []),
        ].map(v=>(
          <button key={v.id} onClick={()=>setView(v.id)} style={{
            padding:'6px 14px',border:`1px solid ${view===v.id?C.violet:C.b1}`,
            background:view===v.id?C.violet+'18':'transparent',color:view===v.id?C.violet:C.t3,
            fontFamily:'JetBrains Mono,monospace',fontSize:9,fontWeight:700,borderRadius:3,cursor:'pointer',
          }}>{v.l}</button>
        ))}
      </div>

      {view==='table'&&(
        <Block>
          <BlockHead label="MODEL LEADERBOARD" accent={C.violet}
            right={<PinButton chart={{id:'compare_leaderboard',type:'bar',title:'Model Leaderboard',subtitle:taskType}} small/>}
          />
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
              <thead><tr style={{background:C.deep}}>
                <th style={TH}>#</th><th style={TH}>MODEL</th>
                {metrics.map(m=><th key={m} style={TH}>{m.toUpperCase()}</th>)}
                <th style={TH}>TRAIN N</th>
              </tr></thead>
              <tbody>
                {sorted.map((r,i)=>(
                  <tr key={r.algoId} style={{borderBottom:`1px solid ${C.b0}`,background:i===0?r.color+'08':'transparent'}}>
                    <td style={{...TD,color:i===0?C.gold:C.t4,fontWeight:700}}>{i===0?'🏆':i+1}</td>
                    <td style={{...TD,color:r.color,fontWeight:700,borderLeft:`2px solid ${r.color}`}}>{r.algoName}</td>
                    {metrics.map(m=>{
                      const v=r.metrics[m];
                      const best=sorted[0].metrics[m];
                      const isBest=Math.abs(v-best)<1e-6;
                      return<td key={m} style={{...TD,color:isBest?C.green:C.t2,fontWeight:isBest?700:400}}>
                        {taskType==='classification'?`${(v*100).toFixed(1)}%`:v}
                      </td>;
                    })}
                    <td style={{...TD,color:C.t3}}>{r.nTrain}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Block>
      )}

      {view==='bar'&&metrics.map(metric=>(
        <Block key={metric}>
          <BlockHead label={metric.toUpperCase()} accent={C.violet}
            right={<PinButton chart={{id:`compare_bar_${metric}`,type:'bar',title:`Model Comparison — ${metric}`,subtitle:taskType}} small/>}
          />
          <BlockBody>
            <ResponsiveContainer width="100%" height={Math.max(120,sorted.length*32+30)}>
              <BarChart data={[...sorted].sort((a,b)=>b.metrics[metric]-a.metrics[metric]).map(r=>({name:r.algoName,value:r.metrics[metric],color:r.color}))} layout="vertical" barCategoryGap="18%" margin={{top:0,right:80,bottom:0,left:0}}>
                <CartesianGrid strokeDasharray="2 2" stroke={C.b0} horizontal={false}/>
                <XAxis type="number" tick={{fill:C.t4,fontSize:8}}/>
                <YAxis dataKey="name" type="category" tick={{fill:C.t2,fontSize:9}} width={160}/>
                <Tooltip content={<ChartTip/>}/>
                <Bar dataKey="value" name={metric} radius={[0,3,3,0]}>
                  {sorted.map((r,i)=><Cell key={i} fill={r.color}/>)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </BlockBody>
        </Block>
      ))}

      {view==='radar'&&sorted.length>=2&&(()=>{
        const radarData=metrics.map(m=>({
          metric:m.toUpperCase(),
          ...Object.fromEntries(sorted.map(r=>[r.algoName.slice(0,14),+(r.metrics[m]*(taskType==='classification'?100:1)).toFixed(2)])),
        }));
        return(
          <Block>
            <BlockHead label="MULTI-METRIC RADAR COMPARISON" accent={C.violet}
              right={<PinButton chart={{id:'compare_radar',type:'radial',title:'Model Radar Comparison'}} small/>}
            />
            <BlockBody>
              <ResponsiveContainer width="100%" height={360}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke={C.b1}/>
                  <PolarAngleAxis dataKey="metric" tick={{fill:C.t3,fontSize:9}}/>
                  <PolarRadiusAxis tick={{fill:C.t4,fontSize:7}}/>
                  {sorted.slice(0,5).map(r=>(
                    <Radar key={r.algoId} name={r.algoName} dataKey={r.algoName.slice(0,14)} stroke={r.color} fill={r.color} fillOpacity={0.12}/>
                  ))}
                  <Legend wrapperStyle={{fontSize:10,color:C.t2}}/>
                  <Tooltip content={<ChartTip/>}/>
                </RadarChart>
              </ResponsiveContainer>
            </BlockBody>
          </Block>
        );
      })()}

      {view==='confusion' && taskType==='classification' && (
        <Block>
          <BlockHead label="CONFUSION MATRIX COMPARISON" accent={C.violet} />
          <BlockBody>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))',gap:10}}>
              {sorted.map((model) => (
                model.confusion ? (
                  <ConfusionMatrixView
                    key={`compare-cm-${model.algoId}`}
                    confusion={model.confusion}
                    color={model.color}
                    title={`${model.algoName}`}
                  />
                ) : (
                  <div key={`compare-cm-${model.algoId}`} style={{padding:14,background:C.raised,border:`1px solid ${C.b1}`,borderRadius:4,color:C.t4,fontSize:10,fontFamily:'JetBrains Mono,monospace'}}>
                    {model.algoName}: confusion matrix unavailable
                  </div>
                )
              ))}
            </div>
          </BlockBody>
        </Block>
      )}

      <Block>
        <BlockHead label="🏆 RECOMMENDED FINAL MODEL" accent={C.green}/>
        <BlockBody>
          <div style={{display:'flex',gap:20,alignItems:'flex-start',flexWrap:'wrap'}}>
            <div style={{flex:1,minWidth:200}}>
              <div style={{fontFamily:'Syne,sans-serif',fontWeight:800,fontSize:22,color:sorted[0].color,marginBottom:4}}>{sorted[0].algoName}</div>
              <div style={{fontSize:11,color:C.t2,lineHeight:1.8}}>
                {taskType==='regression'?`Best R²=${sorted[0].metrics.r2}, RMSE=${sorted[0].metrics.rmse}, MAE=${sorted[0].metrics.mae}.`:`Best accuracy=${(sorted[0].metrics.accuracy*100).toFixed(1)}%, F1=${(sorted[0].metrics.f1*100).toFixed(1)}%.`}
              </div>
            </div>
            <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
              {Object.entries(sorted[0].metrics).map(([k,v])=>(
                <MBadge key={k} label={k.toUpperCase()} value={taskType==='classification'?`${(v*100).toFixed(1)}%`:v} color={sorted[0].color}/>
              ))}
            </div>
          </div>
        </BlockBody>
      </Block>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TUNE TAB
// ─────────────────────────────────────────────────────────────────────────────
const TUNE_CONFIGS = {
  regression: {
    ridge: { name:'Ridge Regression',  params:[{name:'lambda',values:[0.01,0.1,0.5,1,2,5,10,20]}] },
    rfr:   { name:'Random Forest Reg', params:[{name:'nTrees',values:[5,8,10,15,20]},{name:'maxDepth',values:[2,3,4,5]}] },
    knnr:  { name:'KNN Regressor',     params:[{name:'k',values:[3,5,7,10,15]}] },
    gbr:   { name:'Gradient Boosting', params:[{name:'lr',values:[0.01,0.05,0.1,0.2,0.3]},{name:'rounds',values:[10,20,30,50]}] },
  },
  classification: {
    logr:  { name:'Logistic Regression',   params:[{name:'lr',values:[0.01,0.05,0.1,0.2]},{name:'epochs',values:[100,200,300]}] },
    rfc:   { name:'Random Forest Clf',     params:[{name:'nTrees',values:[5,8,10,15,20]},{name:'maxDepth',values:[2,3,4,5]}] },
    knnc:  { name:'KNN Classifier',        params:[{name:'k',values:[3,5,7,10,15,20]}] },
    gbc:   { name:'Gradient Boosting Clf', params:[{name:'lr',values:[0.01,0.05,0.1,0.2]},{name:'rounds',values:[10,20,30,50]}] },
  },
};

function TuneTab({ ds, config, results }) {
  const { targetCol='', featureCols=[], encodings={}, scalingMethod={} } = config;
  const types=useMemo(()=>inferTypes(ds.headers,ds.rows),[ds]);
  const taskType=useMemo(()=>{
    if(!targetCol) return 'regression';
    const t=types.find(t=>t.col===targetCol);
    const vals=ds.rows.map(r=>r[targetCol]).filter(Boolean);
    const uniq=new Set(vals).size;
    return (t?.type==='categorical'||(uniq<=20&&vals.every(v=>Number.isInteger(parseFloat(v)))))?'classification':'regression';
  },[targetCol,types,ds]);

  const tuneOptions=TUNE_CONFIGS[taskType]||TUNE_CONFIGS.regression;
  const [selectedAlgo,setSelectedAlgo]=useState(Object.keys(tuneOptions)[0]);
  const [tuneResults,setTuneResults]=useState([]);
  const [running,setRunning]=useState(false);
  const algoConf=tuneOptions[selectedAlgo];
  const paramNames=algoConf?.params.map(p=>p.name)||[];
  const primaryMetric=taskType==='classification'?'accuracy':'r2';

  const runTuning=useCallback(async()=>{
    if(!targetCol||featureCols.length===0){return;}
    setRunning(true); setTuneResults([]);
    let matrix;
    try{matrix=buildMatrix(ds,featureCols,targetCol,encodings,scalingMethod);}
    catch(e){setRunning(false);return;}
    const{X,y,isClassification}=matrix;
    const{Xtr,ytr,Xte,yte}=trainTestSplit(X,y,0.2);
    const combos=algoConf.params.reduce((acc,p)=>acc.flatMap(c=>p.values.map(v=>({...c,[p.name]:v}))),[{}]);
    const out=[];
    for(const combo of combos){
      await new Promise(r=>setTimeout(r,0));
      let ypred;
      try{
        if(selectedAlgo==='ridge')   ypred=ridgeRegrPredict(Xtr,ytr,Xte,combo.lambda||1);
        else if(selectedAlgo==='rfr')  ypred=randomForest(Xtr,ytr,Xte,combo.nTrees||10,false);
        else if(selectedAlgo==='gbr')  ypred=gbm(Xtr,ytr,Xte,combo.rounds||25,combo.lr||0.1,false);
        else if(selectedAlgo==='knnr') ypred=knnRegress(Xtr,ytr,Xte,combo.k||5);
        else if(selectedAlgo==='logr'){const m=logisticTrain(Xtr,ytr,combo.lr||0.1,combo.epochs||200);ypred=logisticPredict(Xte,m.w,m.b);}
        else if(selectedAlgo==='rfc')  ypred=randomForest(Xtr,ytr,Xte,combo.nTrees||10,true);
        else if(selectedAlgo==='gbc')  ypred=gbm(Xtr,ytr,Xte,combo.rounds||25,combo.lr||0.1,true);
        else if(selectedAlgo==='knnc') ypred=knnClassify(Xtr,ytr,Xte,combo.k||5);
        else ypred=Xte.map(()=>0);
      }catch(e){continue;}
      const metrics=isClassification?classMetrics(yte,ypred):regressionMetrics(yte,ypred);
      out.push({params:combo,metrics,score:metrics[primaryMetric]});
    }
    out.sort((a,b)=>b.score-a.score);
    setTuneResults(out);
    setRunning(false);
  },[ds,config,selectedAlgo,algoConf,taskType]);

  const lineData=useMemo(()=>{
    if(paramNames.length!==1||tuneResults.length===0) return [];
    return [...tuneResults].sort((a,b)=>a.params[paramNames[0]]-b.params[paramNames[0]]).map(r=>({[paramNames[0]]:r.params[paramNames[0]],score:+r.score.toFixed(4)}));
  },[tuneResults,paramNames]);

  const bestResult=tuneResults[0];

  return(
    <div style={{display:'flex',flexDirection:'column',gap:16}}>
      {!targetCol&&<div style={{background:C.orange+'12',border:`1px solid ${C.orange}44`,borderRadius:4,padding:14,color:C.orange,fontSize:11}}>⚠ Configure target column in FEATURES tab first.</div>}

      <div style={{display:'flex',gap:14,flexWrap:'wrap',alignItems:'flex-end'}}>
        <div>
          <Label>ALGORITHM TO TUNE</Label>
          <select value={selectedAlgo} onChange={e=>{setSelectedAlgo(e.target.value);setTuneResults([]);}} style={{background:C.deep,border:`1px solid ${C.b2}`,color:C.t1,padding:'7px 12px',fontSize:11,fontFamily:'JetBrains Mono,monospace',borderRadius:3,cursor:'pointer',outline:'none'}}>
            {Object.entries(tuneOptions).map(([id,conf])=><option key={id} value={id}>{conf.name}</option>)}
          </select>
        </div>
        <Btn onClick={runTuning} color={C.gold} variant="fill" disabled={running||!targetCol}>{running?'⟳ TUNING…':'⚙ RUN GRID SEARCH'}</Btn>
      </div>

      {algoConf&&(
        <Block>
          <BlockHead label="PARAMETER GRID" accent={C.gold}/>
          <BlockBody>
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              {algoConf.params.map(p=>(
                <div key={p.name} style={{display:'flex',gap:12,alignItems:'center'}}>
                  <span style={{fontFamily:'JetBrains Mono,monospace',fontSize:11,color:C.gold,minWidth:80,fontWeight:700}}>{p.name}</span>
                  <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
                    {p.values.map(v=><span key={v} style={{padding:'3px 9px',background:C.gold+'15',border:`1px solid ${C.gold}44`,borderRadius:3,fontFamily:'JetBrains Mono,monospace',fontSize:10,color:C.gold}}>{v}</span>)}
                  </div>
                </div>
              ))}
              <div style={{fontSize:10,color:C.t4}}>Total combinations: {algoConf.params.reduce((n,p)=>n*p.values.length,1)}</div>
            </div>
          </BlockBody>
        </Block>
      )}

      {bestResult&&(
        <div style={{background:C.green+'10',border:`2px solid ${C.green}44`,borderRadius:4,padding:16}}>
          <div style={{fontSize:9,color:C.green,letterSpacing:'.12em',fontFamily:'JetBrains Mono,monospace',fontWeight:700,marginBottom:8}}>🏆 BEST CONFIGURATION FOUND</div>
          <div style={{display:'flex',gap:16,flexWrap:'wrap',alignItems:'center'}}>
            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
              {Object.entries(bestResult.params).map(([k,v])=>(
                <MBadge key={k} label={k.toUpperCase()} value={v} color={C.gold}/>
              ))}
            </div>
            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
              {Object.entries(bestResult.metrics).map(([k,v])=>(
                <MBadge key={k} label={k.toUpperCase()} value={taskType==='classification'?`${(v*100).toFixed(1)}%`:v} color={C.green}/>
              ))}
            </div>
          </div>
        </div>
      )}

      {lineData.length>1&&(
        <Block>
          <BlockHead label={`${primaryMetric.toUpperCase()} vs ${paramNames[0].toUpperCase()}`} accent={C.gold}
            right={<PinButton chart={{id:`tune_line_${selectedAlgo}`,type:'line',title:`Tuning: ${primaryMetric} vs ${paramNames[0]}`}} small/>}
          />
          <BlockBody>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={lineData}>
                <CartesianGrid strokeDasharray="2 2" stroke={C.b0}/>
                <XAxis dataKey={paramNames[0]} tick={{fill:C.t4,fontSize:9}}/>
                <YAxis tick={{fill:C.t4,fontSize:8}}/>
                <Tooltip content={<ChartTip/>}/>
                <Line type="monotone" dataKey="score" stroke={C.gold} strokeWidth={2} dot={{fill:C.gold,r:4}} name={primaryMetric}/>
              </LineChart>
            </ResponsiveContainer>
          </BlockBody>
        </Block>
      )}

      {tuneResults.length>0&&(
        <Block>
          <BlockHead label={`ALL ${tuneResults.length} COMBINATIONS — sorted by ${primaryMetric.toUpperCase()}`} accent={C.gold}/>
          <div style={{maxHeight:400,overflowY:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
              <thead style={{position:'sticky',top:0,zIndex:1}}>
                <tr style={{background:C.deep}}>
                  <th style={TH}>#</th>
                  {paramNames.map(p=><th key={p} style={{...TH,color:C.gold}}>{p.toUpperCase()}</th>)}
                  {Object.keys(tuneResults[0].metrics).map(m=><th key={m} style={TH}>{m.toUpperCase()}</th>)}
                </tr>
              </thead>
              <tbody>
                {tuneResults.map((r,i)=>(
                  <tr key={i} style={{borderBottom:`1px solid ${C.b0}`,background:i===0?C.green+'08':'transparent'}}>
                    <td style={{...TD,color:i===0?C.green:C.t4,fontWeight:i===0?700:400}}>{i===0?'★':i+1}</td>
                    {paramNames.map(p=><td key={p} style={{...TD,color:C.gold,fontWeight:700}}>{r.params[p]}</td>)}
                    {Object.entries(r.metrics).map(([k,v])=>(
                      <td key={k} style={{...TD,color:i===0?C.green:C.t2}}>{taskType==='classification'?`${(v*100).toFixed(1)}%`:v}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Block>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ML REPORT — rich, section-based, draggable, matches ReportComposer style
// ─────────────────────────────────────────────────────────────────────────────
const ML_REPORT_SECS = [
  { id:'overview',      label:'Experiment Overview',    required:true  },
  { id:'dataset',       label:'Dataset Profile',        required:false },
  { id:'eda_dist',      label:'Distributions',          required:false },
  { id:'eda_stats',     label:'Statistics',             required:false },
  { id:'eda_corr',      label:'Correlations',           required:false },
  { id:'config',        label:'Feature Configuration',  required:false },
  { id:'feat_analysis', label:'Feature Analysis',       required:false },
  { id:'pca',           label:'PCA Analysis',           required:false },
  { id:'matrix',        label:'Training Matrix',        required:false },
  { id:'models',        label:'Model Results',          required:true  },
  { id:'best',          label:'Best Model Deep-Dive',   required:false },
  { id:'comparison',    label:'Model Comparison',       required:false },
  { id:'pinned',        label:'Pinned Charts',          required:false },
  { id:'conclusion',    label:'Conclusion & Next Steps',required:true  },
];

const LEGACY_ML_REPORT_ORDER = [
  'overview',
  'dataset',
  'config',
  'feat_analysis',
  'pca',
  'models',
  'best',
  'comparison',
  'eda_stats',
  'eda_dist',
  'eda_corr',
  'matrix',
  'pinned',
  'conclusion',
];

const DEFAULT_ML_REPORT_ORDER = ML_REPORT_SECS.map(section => section.id);

function normalizeMLReportState(reportState, experiment) {
  const allIds = DEFAULT_ML_REPORT_ORDER;
  const requiredIds = ML_REPORT_SECS.filter(section => section.required).map(section => section.id);
  const requestedSections = Array.isArray(reportState?.sections) ? reportState.sections : allIds;
  const sections = [...new Set([...requestedSections, ...requiredIds])].filter(id => allIds.includes(id));
  const requestedOrder = Array.isArray(reportState?.secOrder) ? reportState.secOrder : allIds;
  const normalizedOrder = requestedOrder.length === LEGACY_ML_REPORT_ORDER.length
    && requestedOrder.every((id, index) => id === LEGACY_ML_REPORT_ORDER[index])
      ? DEFAULT_ML_REPORT_ORDER
      : requestedOrder;
  const secOrder = [...new Set([...normalizedOrder, ...allIds])].filter(id => allIds.includes(id));
  const notes = Array.isArray(reportState?.notes)
    ? reportState.notes
        .filter(note => note && typeof note === 'object' && String(note.text || '').trim())
        .slice(0, 30)
        .map(note => ({
          id: String(note.id || `note_${Date.now()}`),
          text: String(note.text || '').trim(),
          tag: String(note.tag || 'observation'),
          ts: note.ts || new Date().toISOString(),
        }))
    : [];
  const title = typeof reportState?.title === 'string' && reportState.title.trim()
    ? reportState.title.trim()
    : `${experiment?.name || 'ML Experiment'} Report`;
  return { sections, secOrder, notes, title };
}

const NOTE_TAGS = [
  {id:'finding',    label:'FINDING',     color:C.green  },
  {id:'observation',label:'OBSERVATION', color:C.cyan   },
  {id:'hypothesis', label:'HYPOTHESIS',  color:C.violet },
  {id:'warning',    label:'WARNING',     color:C.red    },
  {id:'todo',       label:'TODO',        color:C.gold   },
];
const tagMeta = id => NOTE_TAGS.find(t=>t.id===id)||NOTE_TAGS[0];

function mdValue(value) {
  if (value == null || value === '') return '-';
  const text = Array.isArray(value) ? value.join(', ') : String(value);
  return text.replace(/\|/g, '\\|').replace(/\n/g, ' ').trim() || '-';
}

function mdNumber(value, digits = 3) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '-';
  if (Number.isInteger(num)) return String(num);
  return num.toFixed(digits).replace(/\.?0+$/, '');
}

function mdTable(headers, rows) {
  if (!rows?.length) return '';
  return `| ${headers.map(mdValue).join(' | ')} |\n| ${headers.map(() => '---').join(' | ')} |\n${rows.map(row => `| ${row.map(mdValue).join(' | ')} |`).join('\n')}\n\n`;
}

function mdChartFields(chart) {
  const fields = [
    chart?.col,
    chart?.xCol,
    chart?.yCol,
    chart?.catCol,
    chart?.numCol,
    chart?.dateCol,
    chart?.valueCol,
    chart?.sizeCol,
    ...(Array.isArray(chart?.cols) ? chart.cols : []),
  ].filter(Boolean);
  return [...new Set(fields)].join(', ') || '-';
}

function mdChartType(type) {
  return String(type || 'chart').replace(/_/g, ' ').toUpperCase();
}

function mdCorrelationStrength(value) {
  const abs = Math.abs(Number(value) || 0);
  if (abs >= 0.7) return 'strong';
  if (abs >= 0.4) return 'moderate';
  return 'weak';
}

function NoteModal({ onSave, onClose }) {
  const [text,setText]=useState('');
  const [tag,setTag]=useState('finding');
  const tm=tagMeta(tag);
  return(
    <div onClick={e=>e.target===e.currentTarget&&onClose()} style={{position:'fixed',inset:0,background:'rgba(2,5,8,.88)',zIndex:10000,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{background:C.surface,border:`1px solid ${C.b2}`,borderRadius:6,width:480,maxWidth:'96vw',overflow:'hidden',boxShadow:'0 28px 70px rgba(0,0,0,.8)'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'11px 16px',background:C.deep,borderBottom:`1px solid ${C.b0}`}}>
          <span style={{fontFamily:'JetBrains Mono,monospace',fontSize:10,color:C.gold,letterSpacing:'.12em',fontWeight:700}}>✎ ADD NOTE TO REPORT</span>
          <button onClick={onClose} style={{background:'none',border:'none',color:C.t3,cursor:'pointer',fontSize:20,lineHeight:1}}>×</button>
        </div>
        <div style={{display:'flex',gap:5,padding:'12px 16px 8px',flexWrap:'wrap'}}>
          {NOTE_TAGS.map(t=>(
            <button key={t.id} onClick={()=>setTag(t.id)} style={{padding:'4px 10px',border:`1px solid ${tag===t.id?t.color:C.b1}`,background:tag===t.id?t.color+'20':'transparent',color:tag===t.id?t.color:C.t3,fontFamily:'JetBrains Mono,monospace',fontSize:9,fontWeight:700,borderRadius:2,cursor:'pointer'}}>{t.label}</button>
          ))}
        </div>
        <div style={{margin:'0 16px 8px',padding:'6px 10px',borderLeft:`3px solid ${tm.color}`,background:C.raised,borderRadius:2}}>
          <span style={{fontSize:8,color:tm.color,fontFamily:'JetBrains Mono,monospace',fontWeight:700}}>{tm.label}</span>
        </div>
        <div style={{padding:'0 16px'}}>
          <textarea value={text} onChange={e=>setText(e.target.value)} placeholder="Your finding, observation, or hypothesis…"
            style={{width:'100%',minHeight:90,background:C.deep,border:`1px solid ${C.b2}`,color:C.t1,padding:10,fontSize:12,fontFamily:'inherit',borderRadius:3,resize:'vertical',outline:'none',boxSizing:'border-box',lineHeight:1.75}}/>
        </div>
        <div style={{display:'flex',justifyContent:'flex-end',gap:8,padding:'12px 16px'}}>
          <button onClick={onClose} style={{padding:'6px 16px',background:'none',border:`1px solid ${C.b1}`,color:C.t3,borderRadius:3,cursor:'pointer',fontFamily:'JetBrains Mono,monospace',fontSize:9}}>CANCEL</button>
          <button onClick={()=>{if(text.trim()){onSave({text:text.trim(),tag,id:`note_${Date.now()}`,ts:new Date().toISOString()});onClose();}}}
            style={{padding:'6px 18px',background:tm.color,border:'none',color:C.void,borderRadius:3,cursor:'pointer',fontFamily:'JetBrains Mono,monospace',fontSize:9,fontWeight:700}}>
            ADD TO REPORT
          </button>
        </div>
      </div>
    </div>
  );
}

function MLDragSection({ id, title, color, children, onDragStart, onDragOver, onDrop, isDragging, onRemove, required }) {
  return(
    <div draggable onDragStart={()=>onDragStart(id)} onDragOver={e=>{e.preventDefault();onDragOver(id);}} onDrop={e=>{e.preventDefault();onDrop(id);}}
      data-ml-report-section={id}
      style={{marginBottom:28,opacity:isDragging?.45:1,transition:'opacity .15s'}}>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
        <span style={{cursor:'grab',color:C.t4,fontSize:15,userSelect:'none',flexShrink:0}}>⠿</span>
        <h2 style={{fontFamily:'Syne,sans-serif',fontWeight:800,fontSize:16,color,margin:0,flex:1}}>{title}</h2>
        {!required&&onRemove&&(
          <button onClick={onRemove} style={{background:'none',border:`1px solid ${C.b1}`,color:C.t4,fontSize:11,borderRadius:2,cursor:'pointer',padding:'1px 7px',fontFamily:'JetBrains Mono,monospace'}}>HIDE</button>
        )}
      </div>
      <div style={{height:1,background:`linear-gradient(90deg,${color}55 0%,transparent 80%)`,marginBottom:14}}/>
      {children}
    </div>
  );
}

export function MLReportTab({ experiment, dashboardCards, viewData = null, reportState = null, onStateChange = null, readOnly = false }) {
  const config  = experiment.mlConfig  || {};
  const results = experiment.mlResults || {};
  const models  = Object.values(results);
  const ds      = experiment.ds;
  // In View Report mode, viewData.snapTypes comes from full-dataset col_profiles.
  // In live mode (viewData=null), infer from the full ds.rows as usual.
  const types   = useMemo(()=>{
    if (viewData?.snapTypes?.length) return viewData.snapTypes;
    return ds ? inferTypes(ds.headers,ds.rows) : [];
  },[ds, viewData]);
  const numCols = types.filter(t=>t.type==='numeric').map(t=>t.col);
  const catCols = types.filter(t=>t.type==='categorical').map(t=>t.col);

  const taskType= models[0]?.taskType||'—';
  const pkKey   = taskType==='classification'?'accuracy':'r2';
  const sorted  = [...models].sort((a,b)=>(b.metrics[pkKey]||0)-(a.metrics[pkKey]||0));
  const best    = sorted[0];

  const initialReportState = useMemo(() => normalizeMLReportState(reportState, experiment), [reportState, experiment]);
  const [sections,   setSections]   = useState(() => new Set(initialReportState.sections));
  const [secOrder,   setSecOrder]   = useState(() => initialReportState.secOrder);
  const [notes,      setNotes]      = useState(() => initialReportState.notes);
  const [title,      setTitle]      = useState(() => initialReportState.title);
  const [noteModal,  setNoteModal]  = useState(false);
  const [dragSec,    setDragSec]    = useState(null);
  const reportRef = useRef();

  const has = id => sections.has(id);
  const toggleSec = (id, req) => { if(req)return; setSections(p=>{const n=new Set(p);n.has(id)?n.delete(id):n.add(id);return n;}); };
  const addNote   = note => setNotes(p=>[...p,note]);
  const delNote   = id   => setNotes(p=>p.filter(n=>n.id!==id));

  const handleSecDrop = toId => {
    if(!dragSec||dragSec===toId){setDragSec(null);return;}
    setSecOrder(prev=>{
      const arr=[...prev],si=arr.indexOf(dragSec),ti=arr.indexOf(toId);
      if(si<0||ti<0)return prev;
      arr.splice(si,1);arr.splice(ti,0,dragSec);return arr;
    });
    setDragSec(null);
  };

  const rColor = v => Math.abs(v)>0.7?(v>0?C.green:C.red):Math.abs(v)>0.4?C.cyan:C.t3;

  const featImportance = useMemo(()=>{
    // Use full-dataset precomputed feature importance when available (View Report mode)
    if (viewData?.featImportance?.length) return viewData.featImportance;
    const { targetCol='', featureCols=[] } = config;
    if(!targetCol||!ds||featureCols.length===0) return [];
    return featureCols.slice(0,20).map(col=>{
      const ct=types.find(t=>t.col===col);
      const tt=types.find(t=>t.col===targetCol);
      let imp=0;
      if(ct?.type==='numeric'&&tt?.type==='numeric') imp=Math.abs(pearson(ds.rows,col,targetCol));
      else if(ct?.type==='numeric'){
        const classes=[...new Set(ds.rows.map(r=>r[targetCol]).filter(Boolean))];
        const allV=ds.rows.map(r=>parseFloat(r[col])).filter(v=>!isNaN(v));
        const gm=allV.length?allV.reduce((a,b)=>a+b,0)/allV.length:0;
        const bv=classes.reduce((s,c)=>{
          const gv=ds.rows.filter(r=>r[targetCol]===c).map(r=>parseFloat(r[col])).filter(v=>!isNaN(v));
          return gv.length?s+gv.length*(gv.reduce((a,b)=>a+b,0)/gv.length-gm)**2:s;
        },0)/(allV.length||1);
        const tv=allV.reduce((s,v)=>s+(v-gm)**2,0)/(allV.length||1)||1;
        imp=Math.min(1,bv/tv);
      }
      return { col, type:ct?.type, imp:+imp.toFixed(4) };
    }).sort((a,b)=>b.imp-a.imp);
  },[config,ds,types,viewData]);

  // Null summary — use real null counts from viewData when in View Report mode
  const nullCols = viewData?.nullCols ?? (ds ? ds.headers.map(h=>({h,n:ds.rows.filter(r=>!r[h]||r[h]==='').length})).filter(c=>c.n>0).map(c=>({...c,pct:+(c.n/ds.rows.length*100).toFixed(1)})) : []);
  const totalRows = viewData?.totalRows || ds?.rows?.length || 0;
  const numericStats = useMemo(() => {
    if (!ds) return {};
    return numCols.reduce((acc, col) => ({ ...acc, [col]: colStats(ds.rows, col) }), {});
  }, [ds, numCols.join()]);
  const distributionData = useMemo(() => {
    if (viewData?.distColData) return viewData.distColData;
    if (!ds) return { typeMap: {}, perCol: {} };
    const typeMap = {};
    const perCol = {};
    types.forEach(type => {
      typeMap[type.col] = type.type;
      if (type.type === 'numeric') {
        perCol[type.col] = {
          type: type.type,
          stats: numericStats[type.col] || null,
          counts: null,
          catMeta: null,
        };
        return;
      }
      perCol[type.col] = {
        type: type.type,
        stats: null,
        counts: getValueCounts(ds.rows, type.col, 5),
        catMeta: {
          unique: type.uniqueCount || 0,
          nulls: nullCols.find(col => col.h === type.col)?.n || 0,
        },
      };
    });
    return { typeMap, perCol };
  }, [viewData, ds, types, numericStats, nullCols]);
  const correlationPairs = useMemo(() => {
    if (viewData?.corrState?.allPairs?.length) {
      return [...viewData.corrState.allPairs]
        .map(pair => ({ ...pair, abs: Math.abs(pair.r || 0) }))
        .sort((a, b) => (b.abs || 0) - (a.abs || 0));
    }
    if (!ds || numCols.length < 2) return [];
    const pairs = [];
    for (let i = 0; i < Math.min(numCols.length, 10); i += 1) {
      for (let j = i + 1; j < Math.min(numCols.length, 10); j += 1) {
        const r = pearson(ds.rows.slice(0, 2000), numCols[i], numCols[j]);
        pairs.push({ a: numCols[i], b: numCols[j], r, abs: Math.abs(r) });
      }
    }
    return pairs.sort((a, b) => (b.abs || 0) - (a.abs || 0)).slice(0, 20);
  }, [viewData, ds, numCols.join()]);
  const positiveCorr = correlationPairs.filter(pair => (pair.r || 0) >= 0);
  const negativeCorr = correlationPairs.filter(pair => (pair.r || 0) < 0);

  // PCA projections: use stored projections if present, else recompute from ds.rows (50 sample rows)
  const pcaProjections = useMemo(() => {
    const pca = config.pcaResult;
    if (!pca) return null;
    if (pca.projections?.length) return pca.projections;
    const names = pca.featureNames || [];
    if (!ds?.rows?.length || !names.length) return null;
    const matrix = ds.rows.map(r => names.map(c => { const v = parseFloat(r[c]); return isNaN(v) ? 0 : v; }));
    const result = computePCA(matrix, (pca.components || []).length || 2);
    return result?.projections || null;
  }, [config.pcaResult, ds]);

  useEffect(() => {
    if (readOnly || typeof onStateChange !== 'function') return;
    onStateChange({
      sections: [...sections],
      secOrder,
      notes,
      title: title.trim() || `${experiment?.name || 'ML Experiment'} Report`,
    });
  }, [sections, secOrder, notes, title, readOnly, onStateChange, experiment]);

  const exportMD = () => {
    const numericDistributionRows = numCols
      .map(col => {
        const stats = distributionData?.perCol?.[col]?.stats || numericStats[col];
        if (!stats) return null;
        return [
          col,
          stats.count,
          mdNumber(stats.mean),
          mdNumber(stats.std),
          mdNumber(stats.min),
          mdNumber(stats.q1),
          mdNumber(stats.median),
          mdNumber(stats.q3),
          mdNumber(stats.max),
          mdNumber(stats.skew),
          stats.outlierCount ?? '-',
        ];
      })
      .filter(Boolean);
    const categoricalDistributionRows = catCols
      .map(col => {
        const colData = distributionData?.perCol?.[col];
        const counts = colData?.counts || [];
        const topValues = counts.slice(0, 5).map(item => {
          const pct = item.pct != null ? item.pct : totalRows ? +((item.count / totalRows) * 100).toFixed(1) : 0;
          return `${item.name}: ${item.count} (${pct}%)`;
        }).join('; ');
        return [
          col,
          colData?.catMeta?.unique ?? types.find(type => type.col === col)?.uniqueCount ?? '-',
          colData?.catMeta?.nulls ?? nullCols.find(item => item.h === col)?.n ?? 0,
          topValues || '-',
        ];
      })
      .filter(Boolean);
    const leaderboardRows = sorted.map((model, index) => [
      index === 0 ? '1 (best)' : index + 1,
      model.algoName,
      Object.entries(model.metrics || {}).map(([key, value]) => `${key}: ${taskType === 'classification' ? `${(value * 100).toFixed(1)}%` : mdNumber(value)}`).join('; '),
    ]);
    const bestMetricRows = best
      ? Object.entries(best.metrics || {}).map(([key, value]) => [key.toUpperCase(), taskType === 'classification' ? `${(value * 100).toFixed(1)}%` : mdNumber(value)])
      : [];
    const featureRows = featImportance.map((feature, index) => [
      index + 1,
      feature.col,
      feature.type || '-',
      `${(feature.imp * 100).toFixed(1)}%`,
    ]);
    const positiveCorrRows = positiveCorr.map(pair => [pair.a, pair.b, mdNumber(pair.r), mdCorrelationStrength(pair.r)]);
    const negativeCorrRows = negativeCorr.map(pair => [pair.a, pair.b, mdNumber(pair.r), mdCorrelationStrength(pair.r)]);
    const pinnedChartRows = (dashboardCards || []).map(card => [
      card.chart?.title || card.id,
      mdChartType(card.chart?.type),
      mdChartFields(card.chart),
      card.chart?.subtitle || '-',
      card.annotation?.text || '-',
    ]);

    const reportTitle = title.trim() || `${experiment?.name || 'ML Experiment'} Report`;
    const safeFileTitle = reportTitle.replace(/[\\/:*?"<>|]+/g, '-').trim() || 'ml-report';
    let md = `# ${reportTitle}\n\n**${experiment.name}** · ${new Date().toDateString()}\n\n---\n\n`;

    if (has('overview') || has('dataset')) {
      md += `## Dataset\n\n`;
      md += `- File: \`${ds?.name || 'dataset'}\`\n`;
      md += `- Shape: ${ds?.rows?.length || 0} x ${ds?.headers?.length || 0}\n`;
      md += `- Numeric columns: ${numCols.length}\n`;
      md += `- Categorical columns: ${catCols.length}\n`;
      md += `- Columns with nulls: ${nullCols.length}\n\n`;
    }

    if (has('config')) {
      md += `## Configuration\n\n`;
      md += `- Target: \`${config.targetCol || '-'}\`\n`;
      md += `- Task: ${taskType}\n`;
      md += `- Features: ${(config.featureCols || []).join(', ') || '-'}\n\n`;
    }

    if (has('models') && leaderboardRows.length) {
      md += `## Models Trained\n\n`;
      md += mdTable(['Rank', 'Model', 'Metrics'], leaderboardRows);
    }

    if (has('best') && best && bestMetricRows.length) {
      md += `## Best Model\n\n`;
      md += `**${best.algoName}**\n\n`;
      md += mdTable(['Metric', 'Value'], bestMetricRows);
    }

    if (has('feat_analysis') && featureRows.length) {
      md += `## Feature Analysis\n\n`;
      md += mdTable(['Rank', 'Feature', 'Type', 'Importance'], featureRows);
    }

    if (has('eda_stats') && numericDistributionRows.length) {
      md += `## Statistics\n\n`;
      md += mdTable(['Column', 'Count', 'Mean', 'Std', 'Min', 'Q1', 'Median', 'Q3', 'Max', 'Skew', 'Outliers'], numericDistributionRows);
    }

    if (has('eda_dist')) {
      md += `## Distributions\n\n`;
      if (numericDistributionRows.length) {
        md += `### Numeric Columns\n\n`;
        md += mdTable(['Column', 'Count', 'Mean', 'Std', 'Min', 'Q1', 'Median', 'Q3', 'Max', 'Skew', 'Outliers'], numericDistributionRows);
      }
      if (categoricalDistributionRows.length) {
        md += `### Categorical Columns\n\n`;
        md += mdTable(['Column', 'Unique', 'Nulls', 'Top Values'], categoricalDistributionRows);
      }
    }

    if (has('eda_corr') && (positiveCorrRows.length || negativeCorrRows.length)) {
      md += `## Correlations\n\n`;
      if (positiveCorrRows.length) {
        md += `### Positive Relationships\n\n`;
        md += mdTable(['Column A', 'Column B', 'r', 'Strength'], positiveCorrRows);
      }
      if (negativeCorrRows.length) {
        md += `### Negative Relationships\n\n`;
        md += mdTable(['Column A', 'Column B', 'r', 'Strength'], negativeCorrRows);
      }
    }

    if (has('pinned')) {
      md += `## Visualization Inventory\n\n`;
      md += pinnedChartRows.length
        ? mdTable(['Title', 'Type', 'Fields', 'Subtitle', 'Annotation'], pinnedChartRows)
        : `No pinned charts are currently attached to this report.\n\n`;
    }

    if (notes.length > 0) {
      md += `## Research Notes\n\n`;
      notes.forEach(note => {
        md += `**[${note.tag.toUpperCase()}]** ${note.text}\n\n`;
      });
    }

    md += `## Conclusion\n\nML Experiment on \`${ds?.name||'dataset'}\` complete.${best?` Best model: **${best.algoName}** (${pkKey}=${taskType==='classification'?`${(best.metrics[pkKey]*100).toFixed(1)}%`:best.metrics[pkKey]}).`:''} ${sorted.length} model(s) trained.`;
    usageDone('ml_experiments', 'report_download', 1, { format: 'markdown' });
    const a=Object.assign(document.createElement('a'),{href:URL.createObjectURL(new Blob([md],{type:'text/markdown'})),download:`${safeFileTitle}.md`});
    a.click();
  };

  const exportHTML = () => {
    const el = reportRef.current;
    if (!el) return;

    const clone = el.cloneNode(true);
    const sourceCanvases = el.querySelectorAll('canvas');
    const cloneCanvases = clone.querySelectorAll('canvas');
    cloneCanvases.forEach((canvas, idx) => {
      const sourceCanvas = sourceCanvases[idx];
      if (!sourceCanvas) return;
      try {
        const image = document.createElement('img');
        image.src = sourceCanvas.toDataURL('image/png');
        image.alt = 'Chart snapshot';
        image.style.width = '100%';
        image.style.height = '100%';
        image.style.maxWidth = '100%';
        image.style.objectFit = 'contain';
        canvas.replaceWith(image);
      } catch (_) {
        canvas.remove();
      }
    });
    clone.querySelectorAll('svg').forEach((svg) => {
      if (!svg.getAttribute('xmlns')) svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    });

    const getSection = (sectionId) => clone.querySelector(`[data-ml-report-section="${sectionId}"]`);
    const contentNodes = (section) => Array.from(section?.children || []).slice(2);
    const insertMount = (sectionId, mountId, replaceContent = false) => {
      const section = getSection(sectionId);
      if (!section) return;
      if (replaceContent) contentNodes(section).forEach((node) => node.remove());
      const mount = document.createElement('div');
      mount.id = mountId;
      mount.className = 'ml-export-inline';
      mount.style.margin = '0 0 14px';
      const divider = section.children[1];
      if (divider?.nextSibling) section.insertBefore(mount, divider.nextSibling);
      else section.appendChild(mount);
    };

    const stripStaticCharts = (sectionId) => {
      const section = getSection(sectionId);
      if (!section) return;
      section.querySelectorAll('.recharts-responsive-container').forEach((node) => node.remove());
      section.querySelectorAll('.echarts-for-react').forEach((node) => node.remove());
    };

    insertMount('comparison', 'ml-export-comparison', true);
    insertMount('eda_dist', 'ml-export-distributions', true);
    insertMount('eda_corr', 'ml-export-correlations', true);
    if (featImportance.length) {
      insertMount('feat_analysis', 'ml-export-features');
      stripStaticCharts('feat_analysis');
    }
    if (config.pcaResult) {
      insertMount('pca', 'ml-export-pca');
      stripStaticCharts('pca');
    }

    const corrState = (() => {
      if (viewData?.corrState?.matCols?.length) return viewData.corrState;
      if (!ds || numCols.length < 2) return null;
      const matCols = numCols.slice(0, Math.min(numCols.length, 10));
      const matrix = matCols.map((a) => matCols.map((b) => (a === b ? 1 : pearson(ds.rows.slice(0, 2000), a, b))));
      const allPairs = [];
      for (let i = 0; i < matCols.length; i += 1) {
        for (let j = i + 1; j < matCols.length; j += 1) {
          const step = ds.rows.length > 400 ? Math.ceil(ds.rows.length / 400) : 1;
          const pts = ds.rows
            .filter((_, idx) => idx % step === 0)
            .map((row) => {
              const x = parseFloat(row[matCols[i]]);
              const y = parseFloat(row[matCols[j]]);
              return Number.isFinite(x) && Number.isFinite(y) ? [x, y] : null;
            })
            .filter(Boolean)
            .slice(0, 400);
          allPairs.push({ a: matCols[i], b: matCols[j], r: matrix[i][j], pts });
        }
      }
      return { matCols, matrix, allPairs };
    })();

    const interactivePayload = {
      comparisonMetrics: Object.keys(sorted[0]?.metrics || {}).map((metric) => ({
        metric,
        rows: [...sorted]
          .sort((a, b) => (b.metrics?.[metric] || 0) - (a.metrics?.[metric] || 0))
          .map((model) => ({
            name: model.algoName,
            value: Number(model.metrics?.[metric] || 0),
            color: model.color,
          })),
      })),
      featureImportance: featImportance.slice(0, 20).map((feature, idx) => ({
        col: feature.col,
        value: +(feature.imp * 100).toFixed(2),
        color: PALETTE[idx % PALETTE.length],
        type: feature.type,
      })),
      distributions: {
        numeric: numCols.slice(0, 4).map((col, idx) => ({
          id: `num_${idx}`,
          col,
          hist: viewData?.distColData?.perCol?.[col]?.hist || getHistogram(ds.rows, col, 20),
          stats: numericStats[col] || null,
        })).filter((entry) => entry.hist?.length),
        categorical: catCols.slice(0, 4).map((col, idx) => ({
          id: `cat_${idx}`,
          col,
          counts: distributionData?.perCol?.[col]?.counts || getValueCounts(ds.rows, col, 12),
        })).filter((entry) => entry.counts?.length),
      },
      correlations: corrState,
      pca: config.pcaResult ? {
        components: (config.pcaResult.components || []).map((component, idx) => ({
          name: component.name || `PC${idx + 1}`,
          variancePct: component.variancePct,
          loadings: (component.loadings || []).map((loading, loadIdx) => ({
            col: (config.pcaResult.featureNames || [])[loadIdx] || `f${loadIdx}`,
            value: +(loading.loading || 0).toFixed(4),
          })),
        })),
        projections: (pcaProjections || []).slice(0, 400).map((point) => [+(point[0] || 0).toFixed(4), +(point[1] || 0).toFixed(4)]),
      } : null,
      confusion: best?.confusion?.labels?.length ? best.confusion : null,
    };

    const payload = JSON.stringify(interactivePayload)
      .replace(/<\//g, '<\\/')
      .replace(/<!--/g, '<\\!--');

    const reportTitle = title.trim() || `${experiment?.name || 'ML Experiment'} Report`;
    const safeFileTitle = reportTitle.replace(/[\\/:*?"<>|]+/g, '-').trim() || 'ml-report';
    const safeTitle = String(reportTitle)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/`/g, '&#96;')
      .replace(/\$\{/g, '&#36;{');
    const body = clone.innerHTML
      .replace(/`/g, '&#96;')
      .replace(/\$\{/g, '&#36;{');
    const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <title>${safeTitle}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;700&family=Syne:wght@400;700;800;900&display=swap" rel="stylesheet">
  <script src="https://cdn.jsdelivr.net/npm/echarts@5.5.1/dist/echarts.min.js"><\/script>
  <style>
    *, *::before, *::after { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; }
    body { background: #020508; color: #e2e8f0; font-family: system-ui, -apple-system, 'Segoe UI', sans-serif; line-height: 1.8; min-height: 100vh; }
    .ml-report-wrap { max-width: 1080px; margin: 0 auto; padding: 48px 36px 80px; }
    h1, h2, h3 { font-family: 'Syne', system-ui, sans-serif; }
    ::-webkit-scrollbar { width: 6px; height: 6px; }
    ::-webkit-scrollbar-track { background: transparent; }
    ::-webkit-scrollbar-thumb { background: #1e293b; border-radius: 3px; }
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    button { display: none !important; }
    input, textarea { pointer-events: none; }
    select { display: none !important; }
    img { max-width: 100%; display: block; }
    table { border-collapse: collapse; width: 100%; }
    td, th { word-break: break-word; }
    h2[data-collapsible] { cursor: pointer; user-select: none; }
    h2[data-collapsible]:hover { opacity: 0.85; }
    .ml-export-inline { width: 100%; }
    .ml-export-stack { display: grid; gap: 14px; }
    .ml-export-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; margin-bottom: 14px; }
    .ml-export-card { background: #0b1220; border: 1px solid #1e293b; border-radius: 6px; padding: 14px; }
    .ml-export-card.ml-wide { min-height: 360px; }
    .ml-card-title { font-size: 9px; color: #94a3b8; letter-spacing: .12em; font-family: 'JetBrains Mono', monospace; margin-bottom: 10px; }
    .ml-chart-lg { height: 320px; }
    .ml-chart-xl { height: 420px; }
    .ml-chart-md { height: 260px; }
    @media (max-width: 900px) {
      .ml-export-grid { grid-template-columns: 1fr; }
      .ml-report-wrap { padding: 30px 18px 56px; }
    }
  </style>
</head>
<body>
  <div class="ml-report-wrap">
    ${body}
  </div>
  <script>
    (function () {
      'use strict';
      var DATA = ${payload};
      var PALETTE = ${JSON.stringify(PALETTE)};
      var COLORS = {
        cyan: ${JSON.stringify(C.cyan)},
        green: ${JSON.stringify(C.green)},
        violet: ${JSON.stringify(C.violet)},
        gold: ${JSON.stringify(C.gold)},
        red: ${JSON.stringify(C.red)},
        t3: ${JSON.stringify(C.t3)},
        b0: ${JSON.stringify(C.b0)}
      };

      function mount(id, option, onClick) {
        var el = document.getElementById(id);
        if (!el || !option || typeof echarts === 'undefined') return null;
        var inst = echarts.init(el, null, { renderer: 'canvas' });
        inst.setOption(option);
        if (onClick) inst.on('click', onClick);
        window.addEventListener('resize', function () { inst.resize(); });
        return inst;
      }

      function setHTML(id, html) {
        var el = document.getElementById(id);
        if (!el) return null;
        el.innerHTML = html;
        return el;
      }

      if (DATA.comparisonMetrics && DATA.comparisonMetrics.length) {
        var comparisonMount = document.getElementById('ml-export-comparison');
        if (comparisonMount) {
          comparisonMount.className = 'ml-export-grid';
          DATA.comparisonMetrics.forEach(function (metric, idx) {
            var card = document.createElement('div');
            var chartId = 'ml-export-compare-' + idx;
            card.className = 'ml-export-card';
            card.innerHTML = '<div class="ml-card-title">' + metric.metric.toUpperCase() + '</div><div id="' + chartId + '" class="ml-chart-md"></div>';
            comparisonMount.appendChild(card);
            mount(chartId, {
              backgroundColor: 'transparent',
              tooltip: { trigger: 'axis', backgroundColor: '#020508', borderColor: '#1e293b', textStyle: { color: '#e2e8f0' } },
              grid: { top: 12, right: 18, bottom: 12, left: 8, containLabel: true },
              xAxis: { type: 'value', axisLabel: { color: COLORS.t3 }, splitLine: { lineStyle: { color: COLORS.b0, type: 'dashed' } } },
              yAxis: { type: 'category', data: metric.rows.map(function (item) { return item.name; }), axisLabel: { color: '#cbd5e1', fontSize: 10 }, axisTick: { show: false }, axisLine: { show: false } },
              series: [{ type: 'bar', data: metric.rows.map(function (item) { return { value: item.value, itemStyle: { color: item.color } }; }), barMaxWidth: 18, itemStyle: { borderRadius: [0, 3, 3, 0] } }]
            });
          });
        }
      }

      if (DATA.featureImportance && DATA.featureImportance.length && setHTML('ml-export-features', '<div class="ml-export-card"><div class="ml-card-title">FEATURE IMPORTANCE</div><div id="ml-export-features-chart" class="ml-chart-lg"></div></div>')) {
        mount('ml-export-features-chart', {
          backgroundColor: 'transparent',
          tooltip: { trigger: 'axis', backgroundColor: '#020508', borderColor: '#1e293b', textStyle: { color: '#e2e8f0' } },
          grid: { top: 12, right: 18, bottom: 12, left: 8, containLabel: true },
          xAxis: { type: 'value', axisLabel: { color: COLORS.t3, formatter: '{value}%' }, splitLine: { lineStyle: { color: COLORS.b0, type: 'dashed' } } },
          yAxis: { type: 'category', data: DATA.featureImportance.map(function (item) { return item.col; }), axisLabel: { color: '#cbd5e1', fontSize: 10 }, axisTick: { show: false }, axisLine: { show: false } },
          series: [{ type: 'bar', data: DATA.featureImportance.map(function (item) { return { value: item.value, itemStyle: { color: item.color } }; }), barMaxWidth: 18, itemStyle: { borderRadius: [0, 3, 3, 0] } }]
        });
      }

      if (DATA.correlations && DATA.correlations.matCols && DATA.correlations.matCols.length) {
        setHTML('ml-export-correlations', '<div class="ml-export-grid"><div class="ml-export-card ml-wide"><div class="ml-card-title">CORRELATION HEATMAP</div><div id="ml-export-heatmap" class="ml-chart-xl"></div></div><div class="ml-export-card ml-wide"><div class="ml-card-title" id="ml-export-scatter-title">CORRELATION SCATTER</div><div id="ml-export-scatter" class="ml-chart-xl"></div></div></div>');
        var cols = DATA.correlations.matCols;
        var pairLookup = {};
        (DATA.correlations.allPairs || []).forEach(function (pair) {
          pairLookup[pair.a + '__' + pair.b] = pair;
          pairLookup[pair.b + '__' + pair.a] = { a: pair.b, b: pair.a, r: pair.r, pts: pair.pts || [] };
        });
        var heat = [];
        for (var r = 0; r < cols.length; r += 1) {
          for (var c = 0; c < cols.length; c += 1) heat.push([c, r, +(DATA.correlations.matrix[r][c] || 0).toFixed(4)]);
        }
        var scatter = mount('ml-export-scatter', { backgroundColor: 'transparent' });
        var setPair = function (pair) {
          if (!scatter || !pair) return;
          var title = document.getElementById('ml-export-scatter-title');
          if (title) title.textContent = pair.a + ' vs ' + pair.b + ' · r=' + pair.r.toFixed(3);
          scatter.setOption({
            backgroundColor: 'transparent',
            tooltip: { trigger: 'item', backgroundColor: '#020508', borderColor: '#1e293b', textStyle: { color: '#e2e8f0' } },
            grid: { top: 12, right: 18, bottom: 28, left: 12, containLabel: true },
            xAxis: { type: 'value', name: pair.a, nameTextStyle: { color: '#94a3b8' }, axisLabel: { color: COLORS.t3 }, splitLine: { show: false } },
            yAxis: { type: 'value', name: pair.b, nameTextStyle: { color: '#94a3b8' }, axisLabel: { color: COLORS.t3 }, splitLine: { lineStyle: { color: COLORS.b0, type: 'dashed' } } },
            series: [{ type: 'scatter', data: pair.pts || [], symbolSize: 6, itemStyle: { color: COLORS.cyan, opacity: 0.6 } }]
          }, true);
        };
        mount('ml-export-heatmap', {
          backgroundColor: 'transparent',
          tooltip: { trigger: 'item', formatter: function (p) { return cols[p.data[1]] + ' ↔ ' + cols[p.data[0]] + '<br>r = ' + p.data[2]; }, backgroundColor: '#020508', borderColor: '#1e293b', textStyle: { color: '#e2e8f0' } },
          grid: { top: 18, right: 18, bottom: 28, left: 58 },
          xAxis: { type: 'category', data: cols, axisLabel: { color: '#cbd5e1', rotate: 30 }, splitArea: { show: true } },
          yAxis: { type: 'category', data: cols, axisLabel: { color: '#cbd5e1' }, splitArea: { show: true } },
          visualMap: { min: -1, max: 1, calculable: false, orient: 'horizontal', left: 'center', bottom: 0, inRange: { color: ['#f87171', '#0b1220', '#4ade80'] }, textStyle: { color: '#94a3b8' } },
          series: [{ type: 'heatmap', data: heat, label: { show: true, color: '#e2e8f0', formatter: function (p) { return p.data[2].toFixed(2); } }, emphasis: { itemStyle: { borderColor: '#fff', borderWidth: 1 } } }]
        }, function (params) {
          var pair = pairLookup[cols[params.data[1]] + '__' + cols[params.data[0]]];
          setPair(pair);
        });
        if (DATA.correlations.allPairs && DATA.correlations.allPairs.length) setPair(DATA.correlations.allPairs[0]);
      }

      var distGrid = setHTML('ml-export-distributions', '<div class="ml-export-stack"><div id="ml-export-dist-grid" class="ml-export-grid"></div><div id="ml-export-cat-grid" class="ml-export-grid"></div></div>') && document.getElementById('ml-export-dist-grid');
      (DATA.distributions && DATA.distributions.numeric || []).forEach(function (item) {
        if (!distGrid) return;
        var card = document.createElement('div');
        card.className = 'ml-export-card';
        card.innerHTML = '<div class="ml-card-title">' + item.col + ' Distribution</div><div id="ml-chart-' + item.id + '" class="ml-chart-md"></div>';
        distGrid.appendChild(card);
        mount('ml-chart-' + item.id, {
          backgroundColor: 'transparent',
          tooltip: { trigger: 'axis', backgroundColor: '#020508', borderColor: '#1e293b', textStyle: { color: '#e2e8f0' } },
          grid: { top: 12, right: 12, bottom: 28, left: 8, containLabel: true },
          xAxis: { type: 'category', data: item.hist.map(function (bin) { return String(bin.label); }), axisLabel: { color: COLORS.t3, fontSize: 8 }, axisTick: { show: false }, axisLine: { lineStyle: { color: '#1e293b' } } },
          yAxis: { type: 'value', axisLabel: { color: COLORS.t3, fontSize: 8 }, splitLine: { lineStyle: { color: COLORS.b0, type: 'dashed' } } },
          series: [{ type: 'bar', data: item.hist.map(function (bin) { return bin.count; }), itemStyle: { color: COLORS.cyan, borderRadius: [2, 2, 0, 0] } }]
        });
      });

      var catGrid = document.getElementById('ml-export-cat-grid');
      (DATA.distributions && DATA.distributions.categorical || []).forEach(function (item) {
        if (!catGrid) return;
        var card = document.createElement('div');
        card.className = 'ml-export-card';
        card.innerHTML = '<div class="ml-card-title">' + item.col + ' Categories</div><div id="ml-chart-' + item.id + '" class="ml-chart-md"></div>';
        catGrid.appendChild(card);
        mount('ml-chart-' + item.id, {
          backgroundColor: 'transparent',
          tooltip: { trigger: 'axis', backgroundColor: '#020508', borderColor: '#1e293b', textStyle: { color: '#e2e8f0' } },
          grid: { top: 12, right: 16, bottom: 12, left: 8, containLabel: true },
          xAxis: { type: 'value', axisLabel: { color: COLORS.t3, fontSize: 8 }, splitLine: { lineStyle: { color: COLORS.b0, type: 'dashed' } } },
          yAxis: { type: 'category', data: item.counts.map(function (entry) { return String(entry.name); }), axisLabel: { color: '#cbd5e1', fontSize: 9 }, axisTick: { show: false }, axisLine: { show: false } },
          series: [{ type: 'bar', data: item.counts.map(function (entry, idx) { return { value: entry.count, itemStyle: { color: PALETTE[idx % PALETTE.length] } }; }), barMaxWidth: 18, itemStyle: { borderRadius: [0, 3, 3, 0] } }]
        });
      });

      var pcaGrid = setHTML('ml-export-pca', '<div id="ml-export-pca-grid" class="ml-export-grid"></div>') && document.getElementById('ml-export-pca-grid');
      if (DATA.pca && DATA.pca.components && DATA.pca.components.length && pcaGrid) {
        var screeCard = document.createElement('div');
        screeCard.className = 'ml-export-card';
        screeCard.innerHTML = '<div class="ml-card-title">PCA Scree Plot</div><div id="ml-pca-scree" class="ml-chart-md"></div>';
        pcaGrid.appendChild(screeCard);
        mount('ml-pca-scree', {
          backgroundColor: 'transparent',
          tooltip: { trigger: 'axis', backgroundColor: '#020508', borderColor: '#1e293b', textStyle: { color: '#e2e8f0' } },
          grid: { top: 12, right: 12, bottom: 24, left: 8, containLabel: true },
          xAxis: { type: 'category', data: DATA.pca.components.map(function (component) { return component.name; }), axisLabel: { color: '#cbd5e1' } },
          yAxis: { type: 'value', axisLabel: { color: COLORS.t3, formatter: '{value}%' }, splitLine: { lineStyle: { color: COLORS.b0, type: 'dashed' } } },
          series: [{ type: 'bar', data: DATA.pca.components.map(function (component, idx) { return { value: component.variancePct, itemStyle: { color: PALETTE[idx % PALETTE.length] } }; }), itemStyle: { borderRadius: [3, 3, 0, 0] } }]
        });

        var scatterCard = document.createElement('div');
        scatterCard.className = 'ml-export-card';
        scatterCard.innerHTML = '<div class="ml-card-title">PC1 vs PC2</div><div id="ml-pca-scatter" class="ml-chart-md"></div>';
        pcaGrid.appendChild(scatterCard);
        mount('ml-pca-scatter', {
          backgroundColor: 'transparent',
          tooltip: { trigger: 'item', backgroundColor: '#020508', borderColor: '#1e293b', textStyle: { color: '#e2e8f0' } },
          grid: { top: 12, right: 12, bottom: 28, left: 12, containLabel: true },
          xAxis: { type: 'value', name: 'PC1', axisLabel: { color: COLORS.t3 }, splitLine: { show: false } },
          yAxis: { type: 'value', name: 'PC2', axisLabel: { color: COLORS.t3 }, splitLine: { lineStyle: { color: COLORS.b0, type: 'dashed' } } },
          series: [{ type: 'scatter', data: DATA.pca.projections || [], symbolSize: 6, itemStyle: { color: COLORS.cyan, opacity: 0.6 } }]
        });

        if (DATA.pca.components[0] && DATA.pca.components[0].loadings && DATA.pca.components[0].loadings.length) {
          var loadingCard = document.createElement('div');
          loadingCard.className = 'ml-export-card';
          loadingCard.innerHTML = '<div class="ml-card-title">PC1 FEATURE LOADINGS</div><div id="ml-pca-loadings" class="ml-chart-md"></div>';
          pcaGrid.appendChild(loadingCard);
          mount('ml-pca-loadings', {
            backgroundColor: 'transparent',
            tooltip: { trigger: 'axis', backgroundColor: '#020508', borderColor: '#1e293b', textStyle: { color: '#e2e8f0' } },
            grid: { top: 12, right: 18, bottom: 12, left: 8, containLabel: true },
            xAxis: { type: 'value', min: -1, max: 1, axisLabel: { color: COLORS.t3 }, splitLine: { lineStyle: { color: COLORS.b0, type: 'dashed' } } },
            yAxis: { type: 'category', data: DATA.pca.components[0].loadings.map(function (item) { return item.col; }), axisLabel: { color: '#cbd5e1', fontSize: 10 }, axisTick: { show: false }, axisLine: { show: false } },
            series: [{ type: 'bar', data: DATA.pca.components[0].loadings.map(function (item) { return { value: item.value, itemStyle: { color: item.value >= 0 ? COLORS.cyan : COLORS.red } }; }), barMaxWidth: 18, itemStyle: { borderRadius: [0, 3, 3, 0] } }]
          });
        }
      }

      document.querySelectorAll('h2').forEach(function (h2) {
        var headRow = h2.parentElement;
        var secWrap = headRow && headRow.parentElement;
        if (!secWrap) return;
        var siblings = Array.from(secWrap.children);
        var headIdx = siblings.indexOf(headRow);
        var content = siblings.slice(headIdx + 1);
        if (!content.length) return;
        var collapsed = false;
        var pill = document.createElement('span');
        pill.textContent = ' \u25be';
        pill.style.cssText = 'font-size:10px;color:#627795;margin-left:5px;font-family:monospace;cursor:pointer;';
        h2.setAttribute('data-collapsible', '1');
        h2.style.cursor = 'pointer';
        h2.appendChild(pill);
        h2.addEventListener('click', function () {
          collapsed = !collapsed;
          content.forEach(function (c) { c.style.display = collapsed ? 'none' : ''; });
          pill.textContent = collapsed ? ' \u25b8' : ' \u25be';
          h2.style.opacity = collapsed ? '0.55' : '1';
        });
      });
    })();
  <\/script>
</body>
</html>`;
    usageDone('ml_experiments', 'report_download', 1, { format: 'html' });
  const a=Object.assign(document.createElement('a'),{href:URL.createObjectURL(new Blob([html],{type:'text/html'})),download:`${safeFileTitle}.html`});
    a.click();
  };

  return (
    <div style={{display:'flex',flexDirection:'column',gap:14}}>

      {/* ── Controls bar ── */}
      {!readOnly && <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap',padding:'10px 14px',background:C.surface,border:`1px solid ${C.b1}`,borderRadius:4}}>
        <div style={{display:'flex',alignItems:'center',gap:6}}>
          <span style={{fontSize:9,color:C.t4,fontFamily:'JetBrains Mono,monospace'}}>TITLE:</span>
          <input value={title} onChange={e=>setTitle(e.target.value)} style={{background:C.deep,border:`1px solid ${C.b2}`,color:C.t1,padding:'4px 8px',fontSize:10,fontFamily:'JetBrains Mono,monospace',borderRadius:3,outline:'none',minWidth:300}}/>
        </div>
        <div style={{display:'flex',gap:3,flexWrap:'wrap'}}>
          {ML_REPORT_SECS.map(s=>(
            <button key={s.id} onClick={()=>toggleSec(s.id,s.required)} style={{
              padding:'3px 8px',border:`1px solid ${has(s.id)?C.orange:C.b1}`,
              background:has(s.id)?C.orange+'15':'transparent',color:has(s.id)?C.orange:C.t4,
              fontFamily:'JetBrains Mono,monospace',fontSize:8,borderRadius:2,
              cursor:s.required?'default':'pointer',opacity:s.required?.6:1,
            }}>{has(s.id)?'✓':'+'}  {s.label}{s.required?' *':''}</button>
          ))}
        </div>
        <div style={{marginLeft:'auto',display:'flex',gap:6}}>
          <button onClick={()=>setNoteModal(true)} style={{padding:'5px 12px',border:`1px solid ${C.gold}`,background:C.gold+'15',color:C.gold,fontFamily:'JetBrains Mono,monospace',fontSize:9,fontWeight:700,borderRadius:3,cursor:'pointer'}}>✎ NOTE</button>
        </div>
      </div>}

      {/* ── Report Canvas ── */}
      <div ref={reportRef} style={{background:C.surface,border:`1px solid ${C.b1}`,borderRadius:6,padding:'32px 36px',lineHeight:1.8}}>
        {/* Report header */}
        <h1 style={{fontFamily:'Syne,sans-serif',fontWeight:800,fontSize:26,color:C.t1,margin:'0 0 4px'}}>{title.trim() || `${experiment?.name || 'ML Experiment'} Report`}</h1>
        <div style={{fontSize:9,color:C.t3,fontFamily:'JetBrains Mono,monospace',marginBottom:6}}>
          ML EXPERIMENT REPORT · {new Date().toLocaleDateString('en',{year:'numeric',month:'long',day:'numeric'})}
          {models.length>0&&` · ${models.length} MODEL${models.length!==1?'S':''} TRAINED`}
          {notes.length>0&&` · ${notes.length} NOTES`}
          {best&&<> · BEST: <span style={{color:best.color}}>{best.algoName}</span></>}
        </div>
        <div style={{height:1,background:`linear-gradient(90deg,${C.orange}44 0%,transparent 80%)`,marginBottom:6}}/>
        {best&&(
          <div style={{height:1,background:`linear-gradient(90deg,${best.color}33 0%,transparent 60%)`,marginBottom:24}}/>
        )}
        <div style={{fontSize:8,color:C.t4,letterSpacing:'.1em',marginBottom:20,fontFamily:'JetBrains Mono,monospace'}}>⠿ DRAG TO REORDER SECTIONS</div>

        {secOrder.map(sid=>{
          if(!has(sid)) return null;
          const sProps={id:sid,onDragStart:setDragSec,onDragOver:()=>{},onDrop:handleSecDrop,isDragging:dragSec===sid,onRemove:()=>toggleSec(sid,false),required:ML_REPORT_SECS.find(s=>s.id===sid)?.required};

          if(sid==='overview') return(
            <MLDragSection key="overview" title="Experiment Overview" color={C.orange} {...sProps}>
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8,marginBottom:14}}>
                <MBadge label="TASK TYPE"      value={(taskType||'—').toUpperCase()} color={taskType==='regression'?C.cyan:C.green}/>
                <MBadge label="TARGET"         value={config.targetCol||'—'}         color={C.orange}/>
                <MBadge label="FEATURES"       value={(config.featureCols||[]).length} color={C.violet}/>
                <MBadge label="MODELS TRAINED" value={models.length}                  color={C.gold}/>
              </div>
              <p style={{fontSize:12,color:C.t2,lineHeight:1.8,margin:0}}>
                Experiment <strong style={{color:C.t1}}>{experiment.name}</strong> trains a <strong style={{color:taskType==='regression'?C.cyan:C.green}}>{taskType}</strong> model
                {config.targetCol&&<> to predict <strong style={{color:C.orange}}>{config.targetCol}</strong></>}
                {(config.featureCols||[]).length>0&&<> using {config.featureCols.length} feature{config.featureCols.length!==1?'s':''}</>}.
                {models.length>0&&<> {models.length} algorithm{models.length!==1?'s were':' was'} evaluated.</>}
                {best&&<> Best performer: <strong style={{color:best.color}}>{best.algoName}</strong>.</>}
              </p>
            </MLDragSection>
          );

          if(sid==='dataset'&&ds) return(
            <MLDragSection key="dataset" title="Dataset Profile" color={C.cyan} {...sProps}>
              <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:8,marginBottom:14}}>
                <MBadge label="ROWS"         value={(viewData?.totalRows || ds.rows.length).toLocaleString()} color={C.cyan}/>
                <MBadge label="COLUMNS"      value={ds.headers.length}               color={C.green}/>
                <MBadge label="NUMERIC"      value={numCols.length}                  color={C.violet}/>
                <MBadge label="CATEGORICAL"  value={catCols.length}                  color={C.gold}/>
                <MBadge label="NULL COLS"    value={nullCols.length}                 color={nullCols.length>0?C.red:C.green}/>
              </div>
              {/* Data preview */}
              <MiniTable rows={ds.rows} headers={ds.headers} title="DATASET SAMPLE" accent={C.cyan} showModeBar={true}/>
              {nullCols.length>0&&(
                <div style={{marginTop:14}}>
                  <div style={{fontSize:9,color:C.red,fontFamily:'JetBrains Mono,monospace',letterSpacing:'.1em',marginBottom:8}}>NULL VALUE COLUMNS</div>
                  {nullCols.map(c=>(
                    <div key={c.h} style={{display:'flex',alignItems:'center',gap:12,padding:'5px 0',borderBottom:`1px solid ${C.b0}`}}>
                      <span style={{fontFamily:'JetBrains Mono,monospace',fontSize:11,color:C.t1,minWidth:150}}>{c.h}</span>
                      <span style={{fontSize:11,color:C.red,minWidth:110}}>{c.n} nulls ({c.pct}%)</span>
                      <div style={{flex:1,height:4,background:C.b1,borderRadius:2}}>
                        <div style={{height:'100%',width:`${Math.min(c.pct,100)}%`,background:c.pct>30?C.red:c.pct>10?C.gold:C.green,borderRadius:2}}/>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </MLDragSection>
          );

          if(sid==='config') return(
            <MLDragSection key="config" title="Feature Configuration" color={C.violet} {...sProps}>
              <div style={{display:'flex',gap:20,flexWrap:'wrap',marginBottom:14}}>
                <div style={{flex:1,minWidth:200}}>
                  <div style={{fontSize:9,color:C.t3,fontFamily:'JetBrains Mono,monospace',marginBottom:8}}>TARGET & TASK</div>
                  <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
                    <MBadge label="TARGET" value={config.targetCol||'—'} color={C.orange}/>
                    <MBadge label="TASK"   value={(taskType||'—').toUpperCase()} color={taskType==='regression'?C.cyan:C.green}/>
                    <MBadge label="ENCODING" value={Object.keys(config.encodings||{}).length+' cols'} color={C.violet}/>
                    <MBadge label="SCALING"  value={Object.keys(config.scalingMethod||{}).length+' cols'} color={C.cyan}/>
                  </div>
                </div>
              </div>
              {(config.featureCols||[]).length>0&&(
                <div>
                  <div style={{fontSize:9,color:C.t3,fontFamily:'JetBrains Mono,monospace',marginBottom:8}}>FEATURE COLUMNS ({config.featureCols.length})</div>
                  <div style={{display:'flex',flexWrap:'wrap',gap:5,marginBottom:12}}>
                    {config.featureCols.map(c=>{
                      const t=types.find(t=>t.col===c);
                      const color=t?.type==='numeric'?C.cyan:C.gold;
                      const encInfo=config.encodings?.[c];
                      const scaleInfo=config.scalingMethod?.[c];
                      return(
                        <div key={c} style={{padding:'4px 10px',background:C.raised,border:`1px solid ${color}33`,borderRadius:3}}>
                          <div style={{fontSize:10,color,fontFamily:'JetBrains Mono,monospace',fontWeight:700}}>{c}</div>
                          <div style={{fontSize:8,color:C.t4}}>
                            {t?.type}{encInfo?` · enc:${encInfo}`:''}
                            {scaleInfo?` · scale:${scaleInfo}`:''}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </MLDragSection>
          );

          if(sid==='matrix'&&ds&&(config.featureCols||[]).length>0) return(
            <MLDragSection key="matrix" title="Training Matrix" color={C.green} {...sProps}>
              <p style={{fontSize:12,color:C.t2,lineHeight:1.8,margin:'0 0 12px'}}>
                The feature matrix fed to algorithms: {(config.featureCols||[]).length} features → encoded → scaled → bias column prepended. Numeric values ready for ML.
              </p>
              <MiniTable
                rows={ds.rows.slice(0,20).map((r,i)=>{
                  const obj={[`y_${config.targetCol}`]:r[config.targetCol]};
                  (config.featureCols||[]).forEach(c=>{ obj[c]=r[c]; });
                  return obj;
                })}
                headers={[`y_${config.targetCol}`,...(config.featureCols||[]).slice(0,8)]}
                title="FEATURE COLUMNS SNAPSHOT (raw — before encoding/scaling)"
                accent={C.green}
                showModeBar={true}
              />
            </MLDragSection>
          );

          if(sid==='models'&&models.length>0) return(
            <MLDragSection key="models" title="Model Results" color={C.violet} {...sProps}>
              <div style={{overflowX:'auto',marginBottom:16}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
                  <thead><tr style={{background:C.deep}}>
                    <th style={TH}>#</th><th style={TH}>MODEL</th>
                    {Object.keys(sorted[0].metrics).map(m=><th key={m} style={{...TH,color:C.violet}}>{m.toUpperCase()}</th>)}
                    <th style={TH}>TRAIN N</th><th style={TH}>TEST N</th>
                  </tr></thead>
                  <tbody>
                    {sorted.map((r,i)=>(
                      <tr key={r.algoId} style={{borderBottom:`1px solid ${C.b0}`,background:i===0?r.color+'08':'transparent'}}>
                        <td style={{...TD,color:i===0?C.gold:C.t4,fontWeight:700}}>{i===0?'🏆':i+1}</td>
                        <td style={{...TD,color:r.color,fontWeight:700,borderLeft:`2px solid ${r.color}`}}>{r.algoName}</td>
                        {Object.entries(r.metrics).map(([k,v])=>{
                          const isBest=Math.abs(v-sorted[0].metrics[k])<1e-6;
                          return<td key={k} style={{...TD,color:isBest?C.green:C.t2,fontWeight:isBest?700:400}}>{typeof v==='number'?(taskType==='classification'?`${(v*100).toFixed(1)}%`:v):v}</td>;
                        })}
                        <td style={{...TD,color:C.t3}}>{r.nTrain}</td>
                        <td style={{...TD,color:C.t3}}>{r.nTest}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {sorted.length>=1&&(
                <ResponsiveContainer width="100%" height={Math.max(100,sorted.length*28+30)}>
                  <BarChart data={sorted.map(r=>({name:(r.algoName||'Unknown').slice(0,20),value:r.metrics[pkKey],color:r.color}))} layout="vertical" barCategoryGap="20%" margin={{top:0,right:80,bottom:0,left:0}}>
                    <CartesianGrid strokeDasharray="2 2" stroke={C.b0} horizontal={false}/>
                    <XAxis type="number" tick={{fill:C.t4,fontSize:8}}/>
                    <YAxis dataKey="name" type="category" tick={{fill:C.t2,fontSize:9}} width={170}/>
                    <Tooltip content={<ChartTip/>}/>
                    <Bar dataKey="value" name={pkKey} radius={[0,3,3,0]}>
                      {sorted.map((r,i)=><Cell key={i} fill={r.color}/>)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </MLDragSection>
          );

          if(sid==='best'&&best) return(
            <MLDragSection key="best" title="Best Model Deep-Dive" color={best.color} {...sProps}>
              <div style={{display:'flex',gap:20,alignItems:'flex-start',flexWrap:'wrap',marginBottom:16}}>
                <div style={{flex:1}}>
                  <div style={{fontFamily:'Syne,sans-serif',fontWeight:800,fontSize:22,color:best.color,marginBottom:6}}>{best.algoName}</div>
                  <p style={{fontSize:12,color:C.t2,lineHeight:1.8,margin:0}}>
                    {taskType==='regression'
                      ?<>Explains <strong style={{color:C.green}}>{(best.metrics.r2*100).toFixed(1)}%</strong> of variance (R²={best.metrics.r2}). Average error: RMSE={best.metrics.rmse}, MAE={best.metrics.mae}. Trained on {best.nTrain} samples, tested on {best.nTest}.</>
                      :<>Correctly classifies <strong style={{color:C.green}}>{(best.metrics.accuracy*100).toFixed(1)}%</strong> of test samples. F1-score={best.metrics.f1 !== undefined ? `${(best.metrics.f1*100).toFixed(1)}%`:'—'}. Trained on {best.nTrain}, tested on {best.nTest}.</>}
                  </p>
                </div>
                <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
                  {Object.entries(best.metrics).map(([k,v])=>(
                    <MBadge key={k} label={k.toUpperCase()} value={typeof v==='number'?(taskType==='classification'?`${(v*100).toFixed(1)}%`:v):v} color={best.color}/>
                  ))}
                </div>
              </div>
              {/* vs 2nd best comparison */}
              {sorted.length>=2&&(()=>{
                const second=sorted[1];
                const improvement=(best.metrics[pkKey]-second.metrics[pkKey]);
                return(
                  <div style={{padding:'10px 14px',background:best.color+'10',border:`1px solid ${best.color}33`,borderRadius:4}}>
                    <div style={{fontSize:10,color:best.color,fontFamily:'JetBrains Mono,monospace',fontWeight:700,marginBottom:4}}>vs #{2}: {second.algoName}</div>
                    <div style={{fontSize:11,color:C.t2,lineHeight:1.6}}>
                      <strong style={{color:best.color}}>{best.algoName}</strong> outperforms by <strong style={{color:C.green}}>+{(improvement*(taskType==='classification'?100:1)).toFixed(taskType==='classification'?1:4)}{taskType==='classification'?'%':''}</strong> on {pkKey.toUpperCase()}.
                    </div>
                  </div>
                );
              })()}
            </MLDragSection>
          );

          if(sid==='comparison'&&sorted.length>=1) return(
            <MLDragSection key="comparison" title={sorted.length===1?'Model Performance':'Model Comparison'} color={C.violet} {...sProps}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                {Object.keys(sorted[0]?.metrics||{}).map(metric=>(
                  <Block key={metric}>
                    <BlockHead label={metric.toUpperCase()} accent={C.violet}/>
                    <BlockBody>
                      <ResponsiveContainer width="100%" height={Math.max(80,sorted.length*24+20)}>
                        <BarChart data={[...sorted].sort((a,b)=>b.metrics[metric]-a.metrics[metric]).map(r=>({name:(r.algoName||'Unknown').slice(0,16),value:r.metrics[metric],color:r.color}))} layout="vertical" barCategoryGap="20%" margin={{top:0,right:50,bottom:0,left:0}}>
                          <XAxis type="number" tick={{fill:C.t4,fontSize:7}}/>
                          <YAxis dataKey="name" type="category" tick={{fill:C.t2,fontSize:8}} width={120}/>
                          <Tooltip content={<ChartTip/>}/>
                          <Bar dataKey="value" radius={[0,2,2,0]}>
                            {sorted.map((r,i)=><Cell key={i} fill={r.color}/>)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </BlockBody>
                  </Block>
                ))}
              </div>
            </MLDragSection>
          );

          if(sid==='pinned') return(
            <MLDragSection key="pinned" title="Pinned Charts" color={C.gold} {...sProps}>
              {(dashboardCards||[]).length===0?(
                <div style={{padding:20,textAlign:'center',color:C.t4,background:C.raised,borderRadius:4,border:`2px dashed ${C.b1}`}}>
                  <div style={{fontSize:20,marginBottom:6,opacity:0.3}}>★</div>
                  <div style={{fontSize:11,color:C.t3}}>No charts pinned yet. Pin charts with ☆ from Training, Compare, PCA, or Tune tabs.</div>
                </div>
              ):(
                <div style={{display:'flex',flexWrap:'wrap',gap:10}}>
                  {(dashboardCards||[]).map(c=>(
                    <div key={c.id} style={{padding:'10px 14px',background:C.raised,border:`1px solid ${C.gold}33`,borderRadius:4,borderTop:`2px solid ${C.gold}`,minWidth:180}}>
                      <div style={{fontSize:10,color:C.gold,fontFamily:'JetBrains Mono,monospace',fontWeight:700}}>{c.chart?.title||c.id}</div>
                      {c.chart?.subtitle&&<div style={{fontSize:9,color:C.t4,marginTop:2}}>{c.chart.subtitle}</div>}
                      {c.annotation?.text&&<div style={{fontSize:10,color:C.t2,marginTop:6,lineHeight:1.5,borderTop:`1px solid ${C.b0}`,paddingTop:6}}>{c.annotation.text}</div>}
                    </div>
                  ))}
                </div>
              )}
            </MLDragSection>
          );

          if(sid==='conclusion') return(
            <MLDragSection key="conclusion" title="Conclusion & Next Steps" color={C.green} {...sProps}>
              <p style={{fontSize:12,color:C.t2,lineHeight:1.8,margin:'0 0 14px'}}>
                ML Experiment on <strong style={{color:C.t1}}>{ds?.name||'dataset'}</strong> complete.
                {(config.featureCols||[]).length>0&&<> Trained with {config.featureCols.length} feature{config.featureCols.length!==1?'s':''}.</>}
                {models.length>0&&<> {models.length} algorithm{models.length!==1?'s were':' was'} evaluated.</>}
                {best&&<> Best model: <strong style={{color:best.color}}>{best.algoName}</strong> ({pkKey}={taskType==='classification'?`${(best.metrics[pkKey]*100).toFixed(1)}%`:best.metrics[pkKey]}).</>}
              </p>
              {models.length>0&&(
                <div style={{padding:'12px 16px',background:C.raised,border:`1px solid ${C.green}33`,borderRadius:4,marginBottom:14}}>
                  <div style={{fontSize:9,color:C.green,fontFamily:'JetBrains Mono,monospace',fontWeight:700,marginBottom:8}}>RECOMMENDED NEXT STEPS</div>
                  <ul style={{margin:0,paddingLeft:16,fontSize:11,color:C.t2,lineHeight:2}}>
                    {best&&<li>Tune <strong style={{color:best.color}}>{best.algoName}</strong> hyperparameters via the TUNE tab for further improvement.</li>}
                    {taskType==='regression'&&best&&best.metrics.r2<0.7&&<li>R²={best.metrics.r2} — consider feature engineering: polynomial features, log transforms, or more data.</li>}
                    {taskType==='classification'&&best&&best.metrics.accuracy<0.8&&<li>Accuracy={`${(best.metrics.accuracy*100).toFixed(1)}%`} — check class imbalance, add more features, or try ensemble methods.</li>}
                    <li>Run PCA to understand feature structure and reduce dimensionality if needed.</li>
                    <li>Deploy best model and monitor for data drift.</li>
                  </ul>
                </div>
              )}
              {notes.length>0&&(
                <div>
                  <div style={{fontSize:9,color:C.gold,letterSpacing:'.1em',fontFamily:'JetBrains Mono,monospace',fontWeight:700,marginBottom:10}}>RESEARCH NOTES ({notes.length})</div>
                  {notes.map(note=>{
                    const tm=tagMeta(note.tag);
                    return(
                      <div key={note.id} style={{background:C.surface,border:`1px solid ${tm.color}33`,borderLeft:`3px solid ${tm.color}`,borderRadius:3,padding:'10px 13px',marginBottom:8,position:'relative'}}>
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:5}}>
                          <div style={{display:'flex',alignItems:'center',gap:7}}>
                            <span style={{fontSize:8,color:tm.color,letterSpacing:'.1em',fontFamily:'JetBrains Mono,monospace',fontWeight:700}}>{tm.label}</span>
                            <span style={{fontSize:8,color:C.t4}}>{new Date(note.ts).toLocaleString('en',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'})}</span>
                          </div>
                          {!readOnly && <button onClick={()=>delNote(note.id)} style={{background:'none',border:'none',color:C.red,cursor:'pointer',fontSize:14}}>×</button>}
                        </div>
                        <div style={{fontSize:12,color:C.t1,lineHeight:1.75}}>{note.text}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </MLDragSection>
          );

          if(sid==='eda_stats'&&ds) return(
            <MLDragSection key="eda_stats" title="Statistics" color={C.cyan} {...sProps}>
              <StatisticsLab ds={ds}/>
            </MLDragSection>
          );

          if(sid==='eda_dist'&&ds) return(
            <MLDragSection key="eda_dist" title="Distributions" color={C.green} {...sProps}>
              <DistributionLab ds={ds} precomputedColData={viewData?.distColData || null}/>
            </MLDragSection>
          );

          if(sid==='eda_corr'&&ds) return(
            <MLDragSection key="eda_corr" title="Correlations" color={C.violet} {...sProps}>
              <CorrelationLab ds={ds} precomputedCorrState={viewData?.corrState || null}/>
            </MLDragSection>
          );

          if(sid==='feat_analysis'&&ds) return(
            <MLDragSection key="feat_analysis" title="Feature Analysis" color={C.gold} {...sProps}>
              {featImportance.length>0?(
                <div>
                  <div style={{fontSize:9,color:C.t3,fontFamily:'JetBrains Mono,monospace',letterSpacing:'.1em',marginBottom:12}}>
                    FEATURE IMPORTANCE — TARGET: <span style={{color:C.orange}}>{config.targetCol}</span>
                  </div>
                  <ResponsiveContainer width="100%" height={Math.max(100,featImportance.length*28+30)}>
                    <BarChart data={featImportance} layout="vertical" barCategoryGap="20%" margin={{top:0,right:80,bottom:0,left:0}}>
                      <CartesianGrid strokeDasharray="2 2" stroke={C.b0} horizontal={false}/>
                      <XAxis type="number" domain={[0,1]} tick={{fill:C.t4,fontSize:8}}/>
                      <YAxis dataKey="col" type="category" tick={{fill:C.t2,fontSize:9}} width={150}/>
                      <Tooltip content={<ChartTip/>}/>
                      <Bar dataKey="imp" name="Importance" radius={[0,3,3,0]}>
                        {featImportance.map((_,i)=><Cell key={i} fill={PALETTE[i%PALETTE.length]}/>)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <div style={{marginTop:14,overflowX:'auto'}}>
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
                      <thead><tr style={{background:C.deep}}>
                        {['RANK','FEATURE','TYPE','IMPORTANCE'].map(h=><th key={h} style={TH}>{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {featImportance.map((f,i)=>(
                          <tr key={f.col} style={{borderBottom:`1px solid ${C.b0}`}}>
                            <td style={{...TD,color:i===0?C.gold:C.t4,fontWeight:700}}>{i+1}</td>
                            <td style={{...TD,color:f.type==='numeric'?C.cyan:C.gold,fontFamily:'JetBrains Mono,monospace',fontWeight:700}}>{f.col}</td>
                            <td style={{...TD,color:C.t3}}>{f.type}</td>
                            <td style={{...TD}}>
                              <div style={{display:'flex',alignItems:'center',gap:8}}>
                                <div style={{width:90,height:4,background:C.b1,borderRadius:2}}>
                                  <div style={{height:'100%',width:`${(f.imp*100).toFixed(1)}%`,background:PALETTE[i%PALETTE.length],borderRadius:2}}/>
                                </div>
                                <span style={{fontFamily:'JetBrains Mono,monospace',fontSize:10,color:C.t1}}>{(f.imp*100).toFixed(1)}%</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ):(
                <div style={{padding:14,color:C.t3,fontSize:11,textAlign:'center',background:C.raised,borderRadius:4,border:`2px dashed ${C.b1}`}}>
                  {viewData
                    ? 'Feature importance was not computed when this experiment was published. Re-open, configure features, and re-publish.'
                    : <>No feature importance data — set target and feature columns in the <strong style={{color:C.green}}>FEATURE ENGINEERING</strong> tab first.</>}
                </div>
              )}
            </MLDragSection>
          );

          if(sid==='pca') return(
            <MLDragSection key="pca" title="PCA Analysis" color={C.pink} {...sProps}>
              {config.pcaResult?(
                <>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:16}}>
                    <MBadge label="PC1 VARIANCE"  value={`${config.pcaResult.components[0]?.variancePct}%`}  color={C.cyan}/>
                    <MBadge label="PC2 VARIANCE"  value={`${config.pcaResult.components[1]?.variancePct||0}%`} color={C.green}/>
                    <MBadge label="2D TOTAL"       value={`${((config.pcaResult.components[0]?.variancePct||0)+(config.pcaResult.components[1]?.variancePct||0)).toFixed(1)}%`} color={C.violet}/>
                    <MBadge label="FEATURES USED" value={(config.pcaResult.featureNames||[]).length} color={C.t2}/>
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
                    <Block>
                      <BlockHead label="SCREE PLOT" accent={C.violet}/>
                      <BlockBody>
                        <ResponsiveContainer width="100%" height={180}>
                          <BarChart data={config.pcaResult.components.map(c=>({name:c.name,variance:c.variancePct}))} barCategoryGap="20%">
                            <CartesianGrid strokeDasharray="2 2" stroke={C.b0}/>
                            <XAxis dataKey="name" tick={{fill:C.t3,fontSize:9}}/>
                            <YAxis tick={{fill:C.t4,fontSize:8}} unit="%"/>
                            <Tooltip content={<ChartTip/>}/>
                            <Bar dataKey="variance" name="Variance %" radius={[3,3,0,0]}>
                              {config.pcaResult.components.map((_,i)=><Cell key={i} fill={PALETTE[i%PALETTE.length]}/>)}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </BlockBody>
                    </Block>
                    <Block>
                      <BlockHead label="PC1 vs PC2 PROJECTION" accent={C.cyan}/>
                      <BlockBody>
                        <ResponsiveContainer width="100%" height={180}>
                          <ScatterChart margin={{top:5,right:10,bottom:20,left:0}}>
                            <CartesianGrid strokeDasharray="2 2" stroke={C.b0}/>
                            <XAxis dataKey="x" name="PC1" type="number" tick={{fill:C.t4,fontSize:8}}
                              label={{value:`PC1 (${config.pcaResult.components[0]?.variancePct}%)`,position:'insideBottom',offset:-5,fill:C.t3,fontSize:9}}/>
                            <YAxis dataKey="y" name="PC2" type="number" tick={{fill:C.t4,fontSize:8}}/>
                            <Tooltip content={<ChartTip/>}/>
                            <Scatter data={(pcaProjections||[]).map(p=>({x:+p[0].toFixed(3),y:+(p[1]||0).toFixed(3)}))} fill={C.cyan} opacity={0.55} r={3}/>
                          </ScatterChart>
                        </ResponsiveContainer>
                      </BlockBody>
                    </Block>
                  </div>
                  <Block>
                    <BlockHead label="PC1 & PC2 FEATURE LOADINGS" accent={C.green}/>
                    <BlockBody>
                      <ResponsiveContainer width="100%" height={Math.max(140,(config.pcaResult.featureNames||[]).length*30+40)}>
                        <BarChart
                          data={config.pcaResult.components[0].loadings.map((_,fi)=>({
                            col:(config.pcaResult.featureNames||[])[fi]||`f${fi}`,
                            pc1:+config.pcaResult.components[0].loadings[fi].loading.toFixed(4),
                            pc2:+(config.pcaResult.components[1]?.loadings[fi]?.loading||0).toFixed(4),
                          }))}
                          layout="vertical" barCategoryGap="18%" margin={{top:0,right:20,bottom:0,left:0}}
                        >
                          <CartesianGrid strokeDasharray="2 2" stroke={C.b0} horizontal={false}/>
                          <XAxis type="number" domain={[-1,1]} tick={{fill:C.t4,fontSize:8}}/>
                          <YAxis dataKey="col" type="category" tick={{fill:C.t2,fontSize:9}} width={120}/>
                          <Tooltip content={<ChartTip/>}/>
                          <Legend wrapperStyle={{fontSize:10,color:C.t2}}/>
                          <Bar dataKey="pc1" name="PC1" fill={C.cyan} opacity={0.85} radius={[0,2,2,0]}/>
                          <Bar dataKey="pc2" name="PC2" fill={C.green} opacity={0.85} radius={[0,2,2,0]}/>
                        </BarChart>
                      </ResponsiveContainer>
                    </BlockBody>
                  </Block>
                  <Block>
                    <BlockHead label="EIGENVALUES & CUMULATIVE VARIANCE" accent={C.violet}/>
                    <div style={{overflowX:'auto'}}>
                      <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
                        <thead><tr style={{background:C.deep}}>
                          {['COMPONENT','EIGENVALUE','VARIANCE %','CUMULATIVE %','STATUS'].map(h=><th key={h} style={TH}>{h}</th>)}
                        </tr></thead>
                        <tbody>
                          {config.pcaResult.components.reduce((acc,c,i)=>{
                            const cum=+(i===0?c.variancePct:(acc[i-1]?.cum||0)+c.variancePct).toFixed(2);
                            acc.push({...c,cum}); return acc;
                          },[]).map((c,i)=>(
                            <tr key={c.name} style={{borderBottom:`1px solid ${C.b0}`}}>
                              <td style={{...TD,color:PALETTE[i%PALETTE.length],fontWeight:700}}>{c.name}</td>
                              <td style={{...TD,color:C.t1}}>{c.eigenvalue}</td>
                              <td style={{padding:'6px 11px'}}>
                                <div style={{display:'flex',alignItems:'center',gap:8}}>
                                  <div style={{width:70,height:5,background:C.b1,borderRadius:2}}>
                                    <div style={{height:'100%',width:`${c.variancePct}%`,background:PALETTE[i%PALETTE.length],borderRadius:2}}/>
                                  </div>
                                  <span style={{fontFamily:'JetBrains Mono,monospace',fontSize:10,color:C.t1}}>{c.variancePct}%</span>
                                </div>
                              </td>
                              <td style={{...TD,color:c.cum>=90?C.green:C.t2,fontWeight:c.cum>=90?700:400}}>{c.cum}%</td>
                              <td style={{...TD,color:c.cum>=95?C.green:c.cum>=80?C.gold:C.t3}}>
                                {c.cum>=95?'✓ SUFFICIENT':c.cum>=80?'GOOD':'NEEDS MORE'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Block>
                </>
              ):(
                <div style={{padding:20,textAlign:'center',background:C.raised,borderRadius:4,border:`2px dashed ${C.b1}`}}>
                  <div style={{fontSize:20,marginBottom:6,opacity:0.3}}>⬡</div>
                  <div style={{fontSize:11,color:C.t3}}>
                    {viewData
                      ? 'PCA was not run when this experiment was published. Re-open the experiment, run PCA in Feature Engineering, and re-publish.'
                      : <>No PCA results yet. Go to <strong style={{color:C.pink}}>FEATURE ENGINEERING → PCA</strong> tab and click <strong>RUN PCA</strong>, then come back here.</>}
                  </div>
                </div>
              )}
            </MLDragSection>
          );

          return null;
        })}
      </div>

      <div style={{display:'flex',justifyContent:'center',gap:8,flexWrap:'wrap',width:'100%'}}>
        <button onClick={exportMD} style={{padding:'5px 12px',border:`1px solid ${C.cyan}`,background:C.cyan+'15',color:C.cyan,fontFamily:'JetBrains Mono,monospace',fontSize:9,fontWeight:700,borderRadius:3,cursor:'pointer'}}>EXPORT MD REPORT</button>
        <button onClick={exportHTML} style={{padding:'5px 12px',border:`1px solid ${C.green}`,background:C.green+'15',color:C.green,fontFamily:'JetBrains Mono,monospace',fontSize:9,fontWeight:700,borderRadius:3,cursor:'pointer'}}>EXPORT HTML REPORT</button>
      </div>

      {!readOnly && noteModal&&<NoteModal onSave={addNote} onClose={()=>setNoteModal(false)}/>} 
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ROOT: MLLab
// ─────────────────────────────────────────────────────────────────────────────
export default function MLLab({ ds, subTab, experiment, onUpdate, dashboardCards }) {
  const [config, setConfig] = useState(experiment.mlConfig || {
    targetCol:'', featureCols:[], encodings:{}, scalingMethod:{},
  });
  const [results, setResults] = useState(experiment.mlResults || {});

  useEffect(() => {
    setConfig(experiment.mlConfig || {
      targetCol:'', featureCols:[], encodings:{}, scalingMethod:{},
    });
  }, [experiment.mlConfig, experiment.dataSessionId]);

  useEffect(() => {
    setResults(experiment.mlResults || {});
  }, [experiment.mlResults, experiment.dataSessionId]);

  const handleConfigChange = useCallback(patch => {
    const next = { ...config, ...patch };
    setConfig(next);
    onUpdate({ mlConfig: next });
  }, [config, onUpdate]);

  const handleResultsChange = useCallback(next => {
    setResults(next);
    onUpdate({ mlResults: next });
  }, [onUpdate]);

  const { pinChart, pinnedIds } = usePinContext();

  return (
    <div style={{display:'flex',flexDirection:'column',gap:0}}>
      {subTab==='features'&&<FeatureEngineeringTab ds={ds} config={config} onConfigChange={handleConfigChange}/>}
      {subTab==='train'   &&<TrainingTab ds={ds} config={config} results={results} onResultsChange={handleResultsChange}/>}
      {subTab==='compare' &&<CompareTab results={results}/>}
      {subTab==='tune'    &&<TuneTab ds={ds} config={config} results={results}/>}
      {subTab==='report'  &&<MLReportTab experiment={{...experiment,mlConfig:config,mlResults:results}} dashboardCards={dashboardCards||[]} reportState={experiment.reportState || null} onStateChange={(nextState) => onUpdate({ reportState: nextState, notes: nextState.notes || [] })} readOnly={Boolean(experiment.readOnly)}/>}
    </div>
  );
}
